import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import PipingSOVAnalytics from '../ProgressTracking/Piping/PipingSOVAnalytics';
import PipingSOVAll from '../ProgressTracking/Piping/PipingSOVAll';
import ShopAnalyticsTabs from '../ProgressTracking/Piping/ShopAnalyticsTabs';
import { transformMatrixData } from '../../config/disciplines/piping';
import { isFieldRunShop } from '../../utils/pipingFieldRun';

let _jsPDF = null;
let _autoTable = null;
async function loadPdfLibs() {
  if (!_jsPDF) {
    const [jsPDFMod, autoTableMod] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    _jsPDF = jsPDFMod.default;
    _autoTable = autoTableMod.default;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
}

let _ExcelJS = null;
async function loadExcelLib() {
  if (!_ExcelJS) {
    _ExcelJS = (await import('exceljs')).default;
  }
  return _ExcelJS;
}

function SovAudienceToggle({ value, onChange, hint }) {
  const btn = (id, label) => (
    <button
      type="button"
      onClick={() => onChange(id)}
      style={{
        padding: '0.4rem 0.9rem',
        backgroundColor: value === id ? '#6366f1' : 'transparent',
        color: value === id ? 'white' : '#64748b',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: '600',
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
      {hint && <span style={{ fontSize: '0.75rem', color: '#64748b', marginRight: 'auto' }}>{hint}</span>}
      <div style={{ display: 'inline-flex', gap: '0.25rem', backgroundColor: 'white', borderRadius: '8px', padding: '0.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {btn('client', 'Client (Credits)')}
        {btn('internal', 'Internal (Hours + Credits)')}
      </div>
    </div>
  );
}

// Available metrics and charts configuration
const AVAILABLE_METRICS = [
  { id: 'totalItems', label: 'Total Items', icon: '📋', defaultVisible: true },
  { id: 'completed', label: 'Completed', icon: '✅', defaultVisible: true },
  { id: 'inProgress', label: 'In Progress', icon: '🔄', defaultVisible: true },
  { id: 'notStarted', label: 'Not Started', icon: '⏸️', defaultVisible: true },
];

// Grouping dimensions for the Quantity 3/4 tabs (ids match the backend whitelist).
const QUANTITY_GROUP_OPTIONS = [
  { id: 'area', label: 'Area' },
  { id: 'shop', label: 'Fabrication Shop' },
  { id: 'system', label: 'System' },
  { id: 'subArea', label: 'Sub Area' },
  { id: 'material', label: 'Material' },
  { id: 'loadNumber', label: 'Load Number' },
  { id: 'sov', label: 'SOV' },
];

// Grouping dimensions for the Service (credit) tab. Two levels can be picked;
// the default (Field Run / Shop -> Fluid Code) reproduces the classic layout.
const SERVICE_GROUP_OPTIONS = [
  { id: 'fieldRun', label: 'Field Run / Shop' },
  { id: 'fluidCode', label: 'Fluid Code' },
  { id: 'area', label: 'Area' },
  { id: 'shop', label: 'Fabrication Shop' },
  { id: 'system', label: 'System' },
  { id: 'subArea', label: 'Sub Area' },
  { id: 'material', label: 'Material' },
  { id: 'loadNumber', label: 'Load Number' },
  { id: 'sov', label: 'SOV' },
];
const serviceGroupLabel = (id) => ((SERVICE_GROUP_OPTIONS.find(o => o.id === id) || {}).label || 'Group');

/** Section-relative fab % subtitle. Field Run has no fabrication — N/A, excluded from overall. */
function formatFabSplitSubtitle({ frTotal = 0, shopFab = 0, shopTotal = 0 } = {}) {
  const pct = (a, b) => (b > 0 ? ((Number(a) / Number(b)) * 100).toFixed(1) : '0.0');
  const shop = pct(shopFab, shopTotal);
  const parts = [];
  if (frTotal > 0) parts.push('FR: N/A');
  if (shopTotal > 0) parts.push(`Shop: ${shop}%`);
  if (parts.length === 0) return `${shop}% of shop`;
  parts.push(`Overall: ${shop}%`);
  return parts.join(' | ');
}

/** FR/Shop footage split from quantity rows that carry is_field_run. */
function fabSplitFromQuantityRows(rows) {
  let frFab = 0, frTotal = 0, shopFab = 0, shopTotal = 0;
  (rows || []).forEach((r) => {
    const tot = (parseFloat(r.total_sb_ft) || 0) + (parseFloat(r.total_lb_ft) || 0);
    const fab = (parseFloat(r.fab_sb_ft) || 0) + (parseFloat(r.fab_lb_ft) || 0);
    if (r.is_field_run) { frFab += fab; frTotal += tot; }
    else { shopFab += fab; shopTotal += tot; }
  });
  return { frFab, frTotal, shopFab, shopTotal };
}

/** FR/Shop footage split from enriched quantity/material summary. */
function fabSplitFromQuantitySummary(summary) {
  if (!summary) return { frFab: 0, frTotal: 0, shopFab: 0, shopTotal: 0 };
  return {
    frFab: parseFloat(summary.fr_fab_ft) || 0,
    frTotal: parseFloat(summary.fr_total_ft) || 0,
    shopFab: parseFloat(summary.shop_fab_ft) || 0,
    shopTotal: parseFloat(summary.shop_total_ft) || 0,
  };
}

const AVAILABLE_CHARTS = [
  { id: 'progressRing', label: 'Overall Progress', icon: '🎯', defaultVisible: true, allowTypeChange: false },
  { id: 'progressDistribution', label: 'Progress Distribution', icon: '📊', defaultVisible: true, defaultType: 'bar' },
  { id: 'byStatus', label: 'By Status', icon: '📉', defaultVisible: true, defaultType: 'bar' },
  { id: 'byArea', label: 'Items by Area', icon: '📍', defaultVisible: true, defaultType: 'bar' },
  { id: 'avgProgressByArea', label: 'Avg Progress by Area', icon: '📈', defaultVisible: true, defaultType: 'bar' },
];

/**
 * ProgressAnalyticsDashboard - Analytics Dashboard for Progress Tracking (DisciplineMatrix)
 * Displays progress percentages, completion trends, and discipline breakdown
 */
const ProgressAnalyticsDashboard = ({ data, disciplineName = 'Progress', disciplineId = 'progress', projectId, projectDetails, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [quantityData, setQuantityData] = useState(null);
  const [quantityLoading, setQuantityLoading] = useState(false);
  const [materialData, setMaterialData] = useState(null);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [quantity2Data, setQuantity2Data] = useState(null);
  const [quantity2Loading, setQuantity2Loading] = useState(false);
  // Quantity 3/4 mirror Quantity 1/2 but with a user-selectable grouping dimension.
  const [quantity3Data, setQuantity3Data] = useState(null);
  const [quantity3Loading, setQuantity3Loading] = useState(false);
  const [quantity3GroupBy, setQuantity3GroupBy] = useState('area');
  const [quantity4Data, setQuantity4Data] = useState(null);
  const [quantity4Loading, setQuantity4Loading] = useState(false);
  const [quantity4GroupBy, setQuantity4GroupBy] = useState('area');
  // Service tab two-level grouping (e.g. Area -> SOV). Defaults reproduce the
  // classic layout: Field Run / Shop sections with Fluid Code rows.
  const [serviceGroupBy1, setServiceGroupBy1] = useState('fieldRun');
  const [serviceGroupBy2, setServiceGroupBy2] = useState('fluidCode');
  const [shopSummaryData, setShopSummaryData] = useState(null);
  const [shopSummaryLoading, setShopSummaryLoading] = useState(false);
  const [shopAnalyticsTab, setShopAnalyticsTab] = useState('shop1');
  // As-of-date analytics: when set (YYYY-MM-DD), the dashboard reconstructs the
  // tracker state as it stood at end of that day. null = live.
  const [asOfDate, setAsOfDate] = useState(null);
  const [asOfRows, setAsOfRows] = useState(null);
  const [asOfLoading, setAsOfLoading] = useState(false);
  const [asOfError, setAsOfError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogData, setAuditLogData] = useState(null);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [showTabPicker, setShowTabPicker] = useState(false);
  const [sovViewMode, setSovViewMode] = useState('internal'); // client = credits; internal = hours + credits
  // Hidden tab ids. Sub-tabs use a 'sub:' prefix to avoid colliding with
  // top-level tab ids (e.g. top tab 'quantity' vs Quantity 1 sub-tab 'sub:quantity').
  // Quantity 1, 3 and 4 are hidden by default until the user explicitly shows them.
  // Storage key is versioned (v2) so adding new default-hidden tabs re-applies
  // the defaults once for users with an older saved preference.
  const DEFAULT_HIDDEN_TABS = ['sub:quantity', 'sub:quantity3', 'sub:quantity4'];
  const [hiddenTabs, setHiddenTabs] = useState(() => {
    try {
      const stored = localStorage.getItem(`analytics-hidden-tabs-v2-${disciplineId}`);
      return stored ? new Set(JSON.parse(stored)) : new Set(DEFAULT_HIDDEN_TABS);
    } catch { return new Set(DEFAULT_HIDDEN_TABS); }
  });
  const contentRef = useRef(null);
  const { instance, accounts } = useMsal();

  const isPiping = disciplineId === 'piping';

  const toggleTabVisibility = useCallback((tabId) => {
    setHiddenTabs(prev => {
      const next = new Set(prev);
      if (next.has(tabId)) next.delete(tabId); else next.add(tabId);
      try { localStorage.setItem(`analytics-hidden-tabs-v2-${disciplineId}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, [disciplineId]);

  const resetTabVisibility = useCallback(() => {
    setHiddenTabs(new Set());
    // Persist the empty set (instead of removing the key) so "Show All"
    // also overrides the built-in defaults on the next open.
    try { localStorage.setItem(`analytics-hidden-tabs-v2-${disciplineId}`, JSON.stringify([])); } catch {}
  }, [disciplineId]);

  // Fetch reconstructed rows when an as-of date is selected (piping only for now).
  useEffect(() => {
    if (!asOfDate || !projectId || !isPiping) {
      setAsOfRows(null);
      setAsOfError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setAsOfLoading(true);
      setAsOfError(null);
      try {
        const resp = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}?asOf=${asOfDate}`);
        if (!cancelled) {
          const rows = (resp.data && resp.data.data) || [];
          setAsOfRows(transformMatrixData(rows));
        }
      } catch (err) {
        console.error('Error fetching as-of analytics:', err);
        if (!cancelled) {
          // Keep asOfRows null so Overview falls back to live rows instead of
          // treating a failed reconstruction as "no data".
          setAsOfRows(null);
          setAsOfError(err.response?.data?.error || err.message || 'Failed to reconstruct as-of data');
        }
      } finally {
        if (!cancelled) setAsOfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [asOfDate, projectId, isPiping, instance, accounts]);

  // Rows that feed the client-side analytics (Overview + SOV table). Falls back to live.
  const srcData = asOfDate && asOfRows ? asOfRows : data;
  // Query-string suffix appended to per-tab analytics fetches for as-of reconstruction.
  const asOfQuery = asOfDate ? `?asOf=${asOfDate}` : '';

  const addPdfHeader = useCallback(async (doc) => {
    const pageW = doc.internal.pageSize.getWidth();
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const jobNumber = projectDetails?.job_number || '';
    const { y } = await applyLetterhead(doc, {
      instance,
      accounts,
      jobNumber,
      projectId,
      jobName: projectDetails?.job_name,
      margin: 14,
    });
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(`Generated: ${now}`, pageW - 14, y - 4, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    return y;
  }, [projectDetails, instance, accounts, projectId]);

  const buildQuantityPdf = useCallback(async () => {
    if (!quantityData || !quantityData.rows) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { rows, summary } = quantityData;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();

    let startY = await addPdfHeader(doc);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Piping Quantity Summary`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb, fabAll = gFsb + gFlb, instAll = gIsb + gIlb;
    const pctS = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '0%';
    const fS = (v) => Math.round(v).toLocaleString('en-US') + ' ft';
    const fabSub = formatFabSplitSubtitle({ ...fabSplitFromQuantityRows(rows), overallFab: fabAll, overallTotal: totalAll });

    const cardY = startY + 1;
    const cardW = (pageW - 28 - 16) / 5;
    const cardH = 18;
    const cardData = [
      { label: 'Total ISOs', val: String(parseInt(gs.total_isos) || 0), color: [99, 102, 241] },
      { label: 'Total Footage', val: fS(totalAll), sub: `SB: ${fS(gTsb)} | LB: ${fS(gTlb)}`, color: [30, 64, 175] },
      { label: 'Fabricated', val: fS(fabAll), sub: fabSub, color: [245, 158, 11] },
      { label: 'Installed', val: fS(instAll), sub: `${pctS(instAll, totalAll)} of total`, color: [16, 185, 129] },
      { label: 'Remaining', val: fS(totalAll - instAll), sub: `${pctS(totalAll - instAll, totalAll)} of total`, color: [239, 68, 68] },
    ];
    cardData.forEach((c, i) => {
      const x = 14 + i * (cardW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, cardY, 1.5, cardH, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, cardY + 5);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(c.val, x + 4, cardY + 11);
      if (c.sub) {
        doc.setFontSize(5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(doc.splitTextToSize(c.sub, cardW - 6), x + 4, cardY + 14.5);
      }
    });
    startY = cardY + 22;

    const f = (v) => Math.round(parseFloat(v) || 0).toLocaleString('en-US');
    // Field Run vs Shop Fabricated sections (mirrors the on-screen table).
    const sections = [
      { label: 'Field Run', rows: rows.filter(r => r.is_field_run) },
      { label: 'Shop Fabricated', rows: rows.filter(r => !r.is_field_run) },
    ].filter(s => s.rows.length > 0);
    const showBands = sections.length > 1;

    const tableBody = [];
    sections.forEach(section => {
      if (showBands) {
        tableBody.push([{ content: section.label, colSpan: 10, styles: { fontStyle: 'bold', fillColor: [254, 243, 199], textColor: [180, 83, 9], halign: 'left' } }]);
      }
      section.rows.forEach(r => {
        const totalSb = parseFloat(r.total_sb_ft) || 0;
        const totalLb = parseFloat(r.total_lb_ft) || 0;
        const fabSb = parseFloat(r.fab_sb_ft) || 0;
        const fabLb = parseFloat(r.fab_lb_ft) || 0;
        const instSb = parseFloat(r.installed_sb_ft) || 0;
        const instLb = parseFloat(r.installed_lb_ft) || 0;
        tableBody.push([r.fluid_code, r.iso_count, f(totalSb), f(totalLb), f(fabSb), f(fabLb), f(instSb), f(instLb), f(totalSb - instSb), f(totalLb - instLb)]);
      });
      if (showBands) {
        const t = section.rows.reduce((a, r) => ({
          isos: a.isos + (parseInt(r.iso_count) || 0), tsb: a.tsb + (parseFloat(r.total_sb_ft) || 0), tlb: a.tlb + (parseFloat(r.total_lb_ft) || 0),
          fsb: a.fsb + (parseFloat(r.fab_sb_ft) || 0), flb: a.flb + (parseFloat(r.fab_lb_ft) || 0),
          isb: a.isb + (parseFloat(r.installed_sb_ft) || 0), ilb: a.ilb + (parseFloat(r.installed_lb_ft) || 0),
        }), { isos: 0, tsb: 0, tlb: 0, fsb: 0, flb: 0, isb: 0, ilb: 0 });
        tableBody.push([{ content: `${section.label} - Subtotal`, styles: { fontStyle: 'bold', fillColor: [230, 235, 245], halign: 'left' } },
          { content: t.isos, styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } },
          ...[t.tsb, t.tlb, t.fsb, t.flb, t.isb, t.ilb, t.tsb - t.isb, t.tlb - t.ilb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } }))
        ]);
      }
    });

    tableBody.push([{ content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[gs.total_isos, gTsb, gTlb, gFsb, gFlb, gIsb, gIlb, gTsb - gIsb, gTlb - gIlb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } }))
    ]);

    autoTable(doc, {
      startY,
      head: [['Fluid Code', 'ISOs', 'Total FT (SB)', 'Total FT (LB)', 'Fab FT (SB)', 'Fab FT (LB)', 'Installed FT (SB)', 'Installed FT (LB)', 'Remaining FT (SB)', 'Remaining FT (LB)']],
      body: tableBody,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [quantityData, disciplineName, addPdfHeader]);

  const buildQuantity2Pdf = useCallback(async () => {
    if (!quantity2Data || !quantity2Data.rows) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { rows, summary } = quantity2Data;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();

    let startY = await addPdfHeader(doc);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Piping Quantity Summary (Transport-Based Install)`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb, fabAll = gFsb + gFlb, instAll = gIsb + gIlb;
    const pctS = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '0%';
    const fS = (v) => Math.round(v).toLocaleString('en-US') + ' ft';
    const fabSub = formatFabSplitSubtitle({ ...fabSplitFromQuantityRows(rows), overallFab: fabAll, overallTotal: totalAll });

    const cardY = startY + 1;
    const cardW = (pageW - 28 - 16) / 5;
    const cardH = 18;
    const cardData = [
      { label: 'Total ISOs', val: String(parseInt(gs.total_isos) || 0), color: [99, 102, 241] },
      { label: 'Total Footage', val: fS(totalAll), sub: `SB: ${fS(gTsb)} | LB: ${fS(gTlb)}`, color: [30, 64, 175] },
      { label: 'Fabricated', val: fS(fabAll), sub: fabSub, color: [245, 158, 11] },
      { label: 'Installed (Transport)', val: fS(instAll), sub: `${pctS(instAll, totalAll)} of total`, color: [16, 185, 129] },
      { label: 'Remaining', val: fS(totalAll - instAll), sub: `${pctS(totalAll - instAll, totalAll)} of total`, color: [239, 68, 68] },
    ];
    cardData.forEach((c, i) => {
      const x = 14 + i * (cardW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, cardY, 1.5, cardH, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, cardY + 5);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(c.val, x + 4, cardY + 11);
      if (c.sub) {
        doc.setFontSize(5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(doc.splitTextToSize(c.sub, cardW - 6), x + 4, cardY + 14.5);
      }
    });
    startY = cardY + 22;

    const f = (v) => Math.round(parseFloat(v) || 0).toLocaleString('en-US');
    // Field Run vs Shop Fabricated sections (mirrors the on-screen table).
    const sections = [
      { label: 'Field Run', rows: rows.filter(r => r.is_field_run) },
      { label: 'Shop Fabricated', rows: rows.filter(r => !r.is_field_run) },
    ].filter(s => s.rows.length > 0);
    const showBands = sections.length > 1;

    const tableBody = [];
    sections.forEach(section => {
      if (showBands) {
        tableBody.push([{ content: section.label, colSpan: 10, styles: { fontStyle: 'bold', fillColor: [254, 243, 199], textColor: [180, 83, 9], halign: 'left' } }]);
      }
      section.rows.forEach(r => {
        const totalSb = parseFloat(r.total_sb_ft) || 0;
        const totalLb = parseFloat(r.total_lb_ft) || 0;
        const instSb = parseFloat(r.installed_sb_ft) || 0;
        const instLb = parseFloat(r.installed_lb_ft) || 0;
        tableBody.push([r.fluid_code, r.iso_count, f(totalSb), f(totalLb), f(r.fab_sb_ft), f(r.fab_lb_ft), f(instSb), f(instLb), f(totalSb - instSb), f(totalLb - instLb)]);
      });
      if (showBands) {
        const t = section.rows.reduce((a, r) => ({
          isos: a.isos + (parseInt(r.iso_count) || 0), tsb: a.tsb + (parseFloat(r.total_sb_ft) || 0), tlb: a.tlb + (parseFloat(r.total_lb_ft) || 0),
          fsb: a.fsb + (parseFloat(r.fab_sb_ft) || 0), flb: a.flb + (parseFloat(r.fab_lb_ft) || 0),
          isb: a.isb + (parseFloat(r.installed_sb_ft) || 0), ilb: a.ilb + (parseFloat(r.installed_lb_ft) || 0),
        }), { isos: 0, tsb: 0, tlb: 0, fsb: 0, flb: 0, isb: 0, ilb: 0 });
        tableBody.push([{ content: `${section.label} - Subtotal`, styles: { fontStyle: 'bold', fillColor: [230, 235, 245], halign: 'left' } },
          { content: t.isos, styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } },
          ...[t.tsb, t.tlb, t.fsb, t.flb, t.isb, t.ilb, t.tsb - t.isb, t.tlb - t.ilb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } }))
        ]);
      }
    });

    tableBody.push([{ content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[gs.total_isos, gTsb, gTlb, gFsb, gFlb, gIsb, gIlb, gTsb - gIsb, gTlb - gIlb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } }))
    ]);

    autoTable(doc, {
      startY,
      head: [['Fluid Code', 'ISOs', 'Total FT (SB)', 'Total FT (LB)', 'Fab FT (SB)', 'Fab FT (LB)', 'Installed FT (SB)', 'Installed FT (LB)', 'Remaining FT (SB)', 'Remaining FT (LB)']],
      body: tableBody,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [quantity2Data, disciplineName, addPdfHeader]);

  // PDF for the grouped Quantity 3/4 tabs: sections per selected grouping
  // dimension (group_key) with per-group totals.
  const buildGroupedQuantityPdf = useCallback(async (data, titleSuffix, groupBy) => {
    if (!data || !data.rows) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { rows, summary } = data;
    const groupLabel = (QUANTITY_GROUP_OPTIONS.find(o => o.id === groupBy) || {}).label || 'Group';
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();

    let startY = await addPdfHeader(doc);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Piping Quantity Summary${titleSuffix} (by ${groupLabel})`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb, fabAll = gFsb + gFlb, instAll = gIsb + gIlb;
    const pctS = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '0%';
    const fS = (v) => Math.round(v).toLocaleString('en-US') + ' ft';
    const fabSub = formatFabSplitSubtitle({ ...fabSplitFromQuantitySummary(gs), overallFab: fabAll, overallTotal: totalAll });

    const cardY = startY + 1;
    const cardW = (pageW - 28 - 16) / 5;
    const cardH = 18;
    const cardData = [
      { label: 'Total ISOs', val: String(parseInt(gs.total_isos) || 0), color: [99, 102, 241] },
      { label: 'Total Footage', val: fS(totalAll), sub: `SB: ${fS(gTsb)} | LB: ${fS(gTlb)}`, color: [30, 64, 175] },
      { label: 'Fabricated', val: fS(fabAll), sub: fabSub, color: [245, 158, 11] },
      { label: 'Installed', val: fS(instAll), sub: `${pctS(instAll, totalAll)} of total`, color: [16, 185, 129] },
      { label: 'Remaining', val: fS(totalAll - instAll), sub: `${pctS(totalAll - instAll, totalAll)} of total`, color: [239, 68, 68] },
    ];
    cardData.forEach((c, i) => {
      const x = 14 + i * (cardW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, cardY, 1.5, cardH, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, cardY + 5);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(c.val, x + 4, cardY + 11);
      if (c.sub) {
        doc.setFontSize(5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(doc.splitTextToSize(c.sub, cardW - 6), x + 4, cardY + 14.5);
      }
    });
    startY = cardY + 22;

    const f = (v) => Math.round(parseFloat(v) || 0).toLocaleString('en-US');
    const groupsMap = {};
    rows.forEach(r => { const k = r.group_key || 'Unspecified'; if (!groupsMap[k]) groupsMap[k] = []; groupsMap[k].push(r); });

    const tableBody = [];
    Object.keys(groupsMap).forEach(groupName => {
      const groupRows = groupsMap[groupName];
      tableBody.push([{ content: groupName, colSpan: 10, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 64, 175], halign: 'left' } }]);
      groupRows.forEach(r => {
        const totalSb = parseFloat(r.total_sb_ft) || 0;
        const totalLb = parseFloat(r.total_lb_ft) || 0;
        const instSb = parseFloat(r.installed_sb_ft) || 0;
        const instLb = parseFloat(r.installed_lb_ft) || 0;
        tableBody.push([r.fluid_code, r.iso_count, f(totalSb), f(totalLb), f(r.fab_sb_ft), f(r.fab_lb_ft), f(instSb), f(instLb), f(totalSb - instSb), f(totalLb - instLb)]);
      });
      const t = groupRows.reduce((a, r) => ({
        isos: a.isos + (parseInt(r.iso_count) || 0), tsb: a.tsb + (parseFloat(r.total_sb_ft) || 0), tlb: a.tlb + (parseFloat(r.total_lb_ft) || 0),
        fsb: a.fsb + (parseFloat(r.fab_sb_ft) || 0), flb: a.flb + (parseFloat(r.fab_lb_ft) || 0),
        isb: a.isb + (parseFloat(r.installed_sb_ft) || 0), ilb: a.ilb + (parseFloat(r.installed_lb_ft) || 0),
      }), { isos: 0, tsb: 0, tlb: 0, fsb: 0, flb: 0, isb: 0, ilb: 0 });
      tableBody.push([{ content: `${groupName} - Total`, styles: { fontStyle: 'bold', fillColor: [230, 235, 245], halign: 'left' } },
        { content: t.isos, styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } },
        ...[t.tsb, t.tlb, t.fsb, t.flb, t.isb, t.ilb, t.tsb - t.isb, t.tlb - t.ilb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } }))
      ]);
    });

    tableBody.push([{ content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[gs.total_isos, gTsb, gTlb, gFsb, gFlb, gIsb, gIlb, gTsb - gIsb, gTlb - gIlb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } }))
    ]);

    autoTable(doc, {
      startY,
      head: [[`${groupLabel} / Fluid Code`, 'ISOs', 'Total FT (SB)', 'Total FT (LB)', 'Fab FT (SB)', 'Fab FT (LB)', 'Installed FT (SB)', 'Installed FT (LB)', 'Remaining FT (SB)', 'Remaining FT (LB)']],
      body: tableBody,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [disciplineName, addPdfHeader]);

  const buildQuantity3Pdf = useCallback(
    () => buildGroupedQuantityPdf(quantity3Data, '', quantity3GroupBy),
    [buildGroupedQuantityPdf, quantity3Data, quantity3GroupBy]
  );

  const buildQuantity4Pdf = useCallback(
    () => buildGroupedQuantityPdf(quantity4Data, ' (Transport Install)', quantity4GroupBy),
    [buildGroupedQuantityPdf, quantity4Data, quantity4GroupBy]
  );

  const buildMaterialPdf = useCallback(async () => {
    if (!materialData || !materialData.rows) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { rows, summary } = materialData;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();

    let startY = await addPdfHeader(doc);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Piping Material Summary (by Shop)`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb, fabAll = gFsb + gFlb, instAll = gIsb + gIlb;
    const pctS = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '0%';
    const fS = (v) => Math.round(v).toLocaleString('en-US') + ' ft';

    const cardY = startY + 1;
    const cardW = (pageW - 28 - 16) / 5;
    const cardData = [
      { label: 'Total ISOs', val: String(parseInt(gs.total_isos) || 0), color: [99, 102, 241] },
      { label: 'Total Footage', val: fS(totalAll), sub: `SB: ${fS(gTsb)} | LB: ${fS(gTlb)}`, color: [30, 64, 175] },
      { label: 'Fabricated', val: fS(fabAll), sub: `${pctS(fabAll, totalAll)} of total`, color: [245, 158, 11] },
      { label: 'Installed', val: fS(instAll), sub: `${pctS(instAll, totalAll)} of total`, color: [16, 185, 129] },
      { label: 'Remaining', val: fS(totalAll - instAll), sub: `${pctS(totalAll - instAll, totalAll)} of total`, color: [239, 68, 68] },
    ];
    cardData.forEach((c, i) => {
      const x = 14 + i * (cardW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, 16, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, cardY, 1.5, 16, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, cardY + 5);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(c.val, x + 4, cardY + 11);
      if (c.sub) { doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(148, 163, 184); doc.text(c.sub, x + 4, cardY + 14.5); }
    });
    startY = cardY + 20;

    const f = (v) => Math.round(parseFloat(v) || 0).toLocaleString('en-US');
    const shopGroups = {};
    rows.forEach(r => { if (!shopGroups[r.shop]) shopGroups[r.shop] = []; shopGroups[r.shop].push(r); });

    const tableBody = [];
    const shopNames = Object.keys(shopGroups);
    shopNames.forEach(shop => {
      const shopRows = shopGroups[shop];
      shopRows.forEach(r => {
        const totalSb = parseFloat(r.total_sb_ft) || 0;
        const totalLb = parseFloat(r.total_lb_ft) || 0;
        const instSb = parseFloat(r.installed_sb_ft) || 0;
        const instLb = parseFloat(r.installed_lb_ft) || 0;
        tableBody.push([r.material, r.iso_count, f(totalSb), f(totalLb), f(r.fab_sb_ft), f(r.fab_lb_ft), f(instSb), f(instLb), f(totalSb - instSb), f(totalLb - instLb)]);
      });
      const t = shopRows.reduce((a, r) => ({
        isos: a.isos + (parseInt(r.iso_count) || 0), tsb: a.tsb + (parseFloat(r.total_sb_ft) || 0), tlb: a.tlb + (parseFloat(r.total_lb_ft) || 0),
        fsb: a.fsb + (parseFloat(r.fab_sb_ft) || 0), flb: a.flb + (parseFloat(r.fab_lb_ft) || 0),
        isb: a.isb + (parseFloat(r.installed_sb_ft) || 0), ilb: a.ilb + (parseFloat(r.installed_lb_ft) || 0),
      }), { isos: 0, tsb: 0, tlb: 0, fsb: 0, flb: 0, isb: 0, ilb: 0 });
      tableBody.push([{ content: `${shop} - Total`, colSpan: 1, styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } },
        { content: t.isos, styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } },
        ...[t.tsb, t.tlb, t.fsb, t.flb, t.isb, t.ilb, t.tsb - t.isb, t.tlb - t.ilb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } }))
      ]);
    });

    tableBody.push([{ content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[gs.total_isos, gTsb, gTlb, gFsb, gFlb, gIsb, gIlb, gTsb - gIsb, gTlb - gIlb].map(v => ({ content: f(v), styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } }))
    ]);

    autoTable(doc, {
      startY,
      head: [['Material', 'ISOs', 'Total FT (SB)', 'Total FT (LB)', 'Fab FT (SB)', 'Fab FT (LB)', 'Installed FT (SB)', 'Installed FT (LB)', 'Remaining FT (SB)', 'Remaining FT (LB)']],
      body: tableBody,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [materialData, disciplineName, addPdfHeader]);

  const serviceAnalytics = useMemo(() => {
    const empty = { sections: [], totals: {}, hasData: false, banded: false, fabSplit: null };
    if (!srcData || srcData.length === 0) return empty;

    const getCredit = (row) => parseFloat(row.credit || row.budget_credit || 0) || 0;
    const getEarnedCreditShop = (row) => parseFloat(row.earnedCreditShop || row.earned_credit_shop || 0) || 0;
    const getEarnedCreditField = (row) => parseFloat(row.earnedCreditField || row.earned_credit_field || 0) || 0;

    const hasAnyCredit = srcData.some(r => getCredit(r) > 0);
    if (!hasAnyCredit) return empty;

    // Value of a grouping dimension for a row (rows may be camelCase or snake_case).
    const str = (v) => { const s = v == null ? '' : String(v).trim(); return s || ''; };
    const dimValue = (row, dim) => {
      switch (dim) {
        case 'fieldRun':
          return isFieldRunShop(row.fabricationShop || row.fabrication_shop) ? 'Field Run' : 'Shop Fabricated';
        case 'fluidCode': return str(row.fluidCode || row.fluid_code) || 'Unspecified';
        case 'area': return str(row.area) || 'Unspecified';
        case 'shop': return str(row.fabricationShop || row.fabrication_shop) || 'Unspecified';
        case 'system': return str(row.system) || 'Unspecified';
        case 'subArea': return str(row.subArea || row.sub_area) || 'Unspecified';
        case 'material': return str(row.material1 || row.material_1) || 'Unspecified';
        case 'loadNumber': return str(row.loadNumber || row.load_number) || 'Unspecified';
        case 'sov': return str(row.sov || row.sovCode || row.sov_code) || 'Unassigned';
        default: return 'Unspecified';
      }
    };

    // Two-level aggregation: level 1 -> sections, level 2 -> rows within a section.
    const sectionMap = {};
    let gTot = 0, gFab = 0, gInst = 0, gCount = 0;
    let frFab = 0, frTot = 0, shopFab = 0, shopTot = 0;

    srcData.forEach(row => {
      const secKey = dimValue(row, serviceGroupBy1);
      const rowKey = dimValue(row, serviceGroupBy2);
      const tc = getCredit(row);
      const fabCredit = getEarnedCreditShop(row);
      const instCredit = getEarnedCreditField(row);
      const isFieldRun = isFieldRunShop(row.fabricationShop || row.fabrication_shop);
      const fabEligible = isFieldRun ? 0 : tc;
      if (isFieldRun) { frFab += fabCredit; frTot += tc; }
      else { shopFab += fabCredit; shopTot += tc; }

      if (!sectionMap[secKey]) sectionMap[secKey] = { key: secKey, label: secKey, groupMap: {}, totals: { items: 0, totalCredit: 0, fabCredit: 0, fabEligibleCredit: 0, instCredit: 0 } };
      const sec = sectionMap[secKey];
      if (!sec.groupMap[rowKey]) sec.groupMap[rowKey] = { label: rowKey, items: 0, totalCredit: 0, fabCredit: 0, fabEligibleCredit: 0, instCredit: 0 };
      const g = sec.groupMap[rowKey];
      g.items++; g.totalCredit += tc; g.fabCredit += fabCredit; g.fabEligibleCredit += fabEligible; g.instCredit += instCredit;
      sec.totals.items++; sec.totals.totalCredit += tc; sec.totals.fabCredit += fabCredit; sec.totals.fabEligibleCredit += fabEligible; sec.totals.instCredit += instCredit;
      gTot += tc; gFab += fabCredit; gInst += instCredit; gCount++;
    });

    const lastKeys = ['Unspecified', 'Unassigned'];
    const sortKeys = (a, b) => {
      const aLast = lastKeys.includes(a), bLast = lastKeys.includes(b);
      if (aLast !== bLast) return aLast ? 1 : -1;
      // Field Run always leads its shop counterpart in the classic layout.
      if (a === 'Field Run' && b === 'Shop Fabricated') return -1;
      if (a === 'Shop Fabricated' && b === 'Field Run') return 1;
      return a.localeCompare(b, undefined, { numeric: true });
    };
    const sections = Object.values(sectionMap)
      .sort((a, b) => sortKeys(a.key, b.key))
      .map(sec => ({
        key: sec.key,
        label: sec.label,
        groups: Object.values(sec.groupMap).sort((a, b) => b.totalCredit - a.totalCredit),
        totals: sec.totals,
      }));

    return {
      sections,
      totals: { items: gCount, totalCredit: gTot, fabCredit: gFab, fabEligibleCredit: shopTot, instCredit: gInst },
      hasData: true,
      banded: sections.length > 1,
      fabSplit: { frFab, frTotal: frTot, shopFab, shopTotal: shopTot },
    };
  }, [srcData, serviceGroupBy1, serviceGroupBy2]);

  const buildServicePdf = useCallback(async () => {
    if (!serviceAnalytics.hasData) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { sections, totals, banded } = serviceAnalytics;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const f = (v) => { const n = parseFloat(v) || 0; return n.toLocaleString('en-US', { maximumFractionDigits: 1 }); };
    const pctStr = (a, b) => { const bv = parseFloat(b) || 0; return bv > 0 ? ((parseFloat(a) / bv) * 100).toFixed(1) + '%' : '0%'; };
    const fabSub = formatFabSplitSubtitle({
      ...(serviceAnalytics.fabSplit || {}),
      overallFab: totals.fabCredit,
      overallTotal: totals.totalCredit,
    });

    let startY = await addPdfHeader(doc);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Service (Credit Summary, by ${serviceGroupLabel(serviceGroupBy1)} / ${serviceGroupLabel(serviceGroupBy2)})`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const cardY = startY + 1;
    const cardW = (pageW - 28 - 16) / 5;
    const cardH = 18;
    const cardData = [
      { label: 'Total Items', val: String(totals.items), color: [99, 102, 241] },
      { label: 'Total Credit', val: f(totals.totalCredit), color: [124, 58, 237] },
      { label: 'Fab Earned Credit', val: f(totals.fabCredit), sub: fabSub, color: [245, 158, 11] },
      { label: 'Install Earned Credit', val: f(totals.instCredit), sub: `${pctStr(totals.instCredit, totals.totalCredit)} of total`, color: [16, 185, 129] },
      { label: 'Remaining', val: f(totals.totalCredit - totals.instCredit), sub: `${pctStr(totals.totalCredit - totals.instCredit, totals.totalCredit)} of total`, color: [239, 68, 68] },
    ];
    cardData.forEach((c, i) => {
      const x = 14 + i * (cardW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, cardY, 1.5, cardH, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, cardY + 5);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(c.val, x + 4, cardY + 11);
      if (c.sub) {
        doc.setFontSize(5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(doc.splitTextToSize(c.sub, cardW - 6), x + 4, cardY + 14.5);
      }
    });
    startY = cardY + 22;

    const tableBody = [];
    const rowVals = (g) => [
      String(g.items), f(g.totalCredit), f(g.fabCredit), pctStr(g.fabCredit, g.totalCredit),
      f(g.instCredit), pctStr(g.instCredit, g.totalCredit), f(g.totalCredit - g.instCredit),
    ];
    sections.forEach(section => {
      if (banded) {
        tableBody.push([{ content: section.label, colSpan: 8, styles: { fontStyle: 'bold', fillColor: [254, 243, 199], textColor: [180, 83, 9], halign: 'left' } }]);
      }
      section.groups.forEach(g => tableBody.push([g.label, ...rowVals(g)]));
      if (banded) {
        tableBody.push([
          { content: `${section.label} - Subtotal`, styles: { fontStyle: 'bold', fillColor: [230, 235, 245], halign: 'left' } },
          ...rowVals(section.totals).map(v => ({ content: v, styles: { fontStyle: 'bold', fillColor: [230, 235, 245] } })),
        ]);
      }
    });

    tableBody.push([
      { content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...rowVals(totals).map(v => ({
        content: v, styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] }
      })),
    ]);

    autoTable(doc, {
      startY,
      head: [[serviceGroupLabel(serviceGroupBy2), 'Items', 'Total Credit', 'Fab Earned', 'Fab %', 'Install Earned', 'Install %', 'Remaining']],
      body: tableBody,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [serviceAnalytics, serviceGroupBy1, serviceGroupBy2, disciplineName, addPdfHeader]);

  // Client-side overview analytics (used by Overview tab + PDF/Excel export).
  // Defined here (before the export builders) so the builders can depend on it.
  const analytics = useMemo(() => {
    const data = srcData;
    if (!data || data.length === 0) {
      return { totalRows: 0, avgProgress: 0, completed: 0, inProgress: 0, notStarted: 0, byArea: [], byStatus: [], progressDistribution: [], onHoldItems: [], onHoldCount: 0 };
    }

    const totalRows = data.length;

    const getNum = (row, fields) => {
      for (const field of fields) {
        if (row[field] != null && row[field] !== '') return parseFloat(row[field]) || 0;
      }
      return 0;
    };
    // Piping stores hours/credit split across shop + field (+ insulation/heat tracing).
    // Other disciplines may use simpler aliases — fall back gracefully.
    const getBudgetHrs = (row) => getNum(row, ['budget_hours', 'budgetedHr', 'budgeted_hr']);
    const getEarnedHrs = (row) => {
      const split = getNum(row, ['earned_hours_shop']) + getNum(row, ['earned_hours_field'])
        + getNum(row, ['earned_hours_insulation']) + getNum(row, ['earned_hours_heat_tracing']);
      return split > 0 ? split : getNum(row, ['earnedHr', 'earned_hr', 'earned_hours']);
    };
    const getBudgetCredit = (row) => getNum(row, ['budget_credit', 'credit']);
    const getEarnedCredit = (row) => {
      const split = getNum(row, ['earned_credit_shop']) + getNum(row, ['earned_credit_field']);
      return split > 0 ? split : getNum(row, ['earnedCredit', 'earned_credit']);
    };
    // Per-row completion is credit-weighted (matches the matrix's stated methodology),
    // then hours, then any explicit progress field, then shop/field completion.
    const getProgress = (row) => {
      for (const field of ['progress_percentage', 'progress', 'completion', 'percent_complete', 'overall_progress']) {
        if (row[field] != null && row[field] !== '') return parseFloat(row[field]) || 0;
      }
      const bc = getBudgetCredit(row);
      if (bc > 0) return Math.min(100, (getEarnedCredit(row) / bc) * 100);
      const bh = getBudgetHrs(row);
      if (bh > 0) return Math.min(100, (getEarnedHrs(row) / bh) * 100);
      const shop = getNum(row, ['total_shop_completion']);
      const field = getNum(row, ['field_total_completion', 'total_field_completion']);
      const parts = [shop, field].filter(v => v > 0);
      return parts.length ? parts.reduce((a, b) => a + b, 0) / parts.length : 0;
    };

    // Project-wide earned value (hours & credit), independent of SOV assignment.
    const totalBudgetHrs = data.reduce((s, r) => s + getBudgetHrs(r), 0);
    const totalEarnedHrs = data.reduce((s, r) => s + getEarnedHrs(r), 0);
    const remainingHrs = Math.max(0, totalBudgetHrs - totalEarnedHrs);
    const hrsProgress = totalBudgetHrs > 0 ? (totalEarnedHrs / totalBudgetHrs) * 100 : 0;
    const totalCredit = data.reduce((s, r) => s + getBudgetCredit(r), 0);
    const totalEarnedCredit = data.reduce((s, r) => s + getEarnedCredit(r), 0);
    const creditProgress = totalCredit > 0 ? (totalEarnedCredit / totalCredit) * 100 : 0;
    const hasHoursData = totalBudgetHrs > 0;

    // Piping splits credit into two INDEPENDENT phases (shop fab + field install),
    // each measured against the full budget_credit. They must never be summed into a
    // single percentage (that double-counts and can exceed 100%). Track them separately.
    const getEarnedCreditShop = (row) => getNum(row, ['earned_credit_shop', 'earnedCreditShop']);
    const getEarnedCreditField = (row) => getNum(row, ['earned_credit_field', 'earnedCreditField']);
    const totalEarnedCreditShop = data.reduce((s, r) => s + getEarnedCreditShop(r), 0);
    const totalEarnedCreditField = data.reduce((s, r) => s + getEarnedCreditField(r), 0);
    const hasCreditSplit = totalEarnedCreditShop > 0 || totalEarnedCreditField > 0;
    const fabEligibleCredit = data.reduce((s, r) => (
      s + (isFieldRunShop(r.fabricationShop || r.fabrication_shop) ? 0 : getBudgetCredit(r))
    ), 0);
    const shopCreditProgress = fabEligibleCredit > 0 ? (totalEarnedCreditShop / fabEligibleCredit) * 100 : 0;
    const fieldCreditProgress = totalCredit > 0 ? (totalEarnedCreditField / totalCredit) * 100 : 0;

    const progresses = data.map(getProgress);
    // Overall progress is credit-weighted when credit exists; otherwise a simple mean.
    const avgProgress = totalCredit > 0
      ? creditProgress
      : (progresses.length > 0 ? progresses.reduce((a, b) => a + b, 0) / progresses.length : 0);
    const completed = progresses.filter(p => p >= 99.5).length;
    const inProgress = progresses.filter(p => p > 0 && p < 99.5).length;
    const notStarted = progresses.filter(p => p <= 0).length;

    const areaMap = {};
    data.forEach(d => {
      const area = d.area || d.system || d.discipline || d.section || 'Unspecified';
      if (!areaMap[area]) areaMap[area] = { name: area, count: 0, totalProgress: 0, budgetHrs: 0, earnedHrs: 0 };
      areaMap[area].count++;
      areaMap[area].totalProgress += getProgress(d);
      areaMap[area].budgetHrs += getBudgetHrs(d);
      areaMap[area].earnedHrs += getEarnedHrs(d);
    });
    const byArea = Object.values(areaMap).map(a => ({
      ...a, avgProgress: a.count > 0 ? a.totalProgress / a.count : 0,
    })).sort((a, b) => b.count - a.count).slice(0, 15);

    // Areas ranked by REMAINING budgeted hours — where the effort is concentrated.
    const byAreaRemaining = Object.values(areaMap).map(a => ({
      name: a.name,
      count: a.count,
      remainingHrs: Math.max(0, a.budgetHrs - a.earnedHrs),
    })).filter(a => a.remainingHrs > 0).sort((a, b) => b.remainingHrs - a.remainingHrs).slice(0, 12);

    // "Almost done" items (76-99%) — small effort to close out.
    let almostDoneCount = 0;
    let almostDoneRemainingHrs = 0;
    data.forEach((r) => {
      const p = getProgress(r);
      if (p >= 76 && p < 100) {
        almostDoneCount++;
        almostDoneRemainingHrs += Math.max(0, getBudgetHrs(r) - getEarnedHrs(r));
      }
    });

    const statusMap = {};
    data.forEach(d => {
      const status = d.status || (getProgress(d) >= 100 ? 'Completed' : getProgress(d) > 0 ? 'In Progress' : 'Not Started');
      if (!statusMap[status]) statusMap[status] = { name: status, count: 0 };
      statusMap[status].count++;
    });
    const byStatus = Object.values(statusMap).sort((a, b) => b.count - a.count);

    const ranges = [
      { name: '0%', min: 0, max: 0, count: 0 }, { name: '1-25%', min: 1, max: 25, count: 0 },
      { name: '26-50%', min: 26, max: 50, count: 0 }, { name: '51-75%', min: 51, max: 75, count: 0 },
      { name: '76-99%', min: 76, max: 99, count: 0 }, { name: '100%', min: 100, max: 100, count: 0 },
    ];
    progresses.forEach(p => {
      if (p === 0) ranges[0].count++;
      else if (p <= 25) ranges[1].count++;
      else if (p <= 50) ranges[2].count++;
      else if (p <= 75) ranges[3].count++;
      else if (p < 100) ranges[4].count++;
      else ranges[5].count++;
    });

    // Piping: build SEPARATE shop (fab) and field (install) distributions/counts.
    // Each phase is its own 0-100% measure of budget_credit — never combined.
    const rowShopPct = (r) => { const bc = getBudgetCredit(r); return bc > 0 ? Math.min(100, (getEarnedCreditShop(r) / bc) * 100) : 0; };
    const rowFieldPct = (r) => { const bc = getBudgetCredit(r); return bc > 0 ? Math.min(100, (getEarnedCreditField(r) / bc) * 100) : 0; };
    const buildDist = (pcts) => {
      const r = [
        { name: '0%', count: 0 }, { name: '1-25%', count: 0 }, { name: '26-50%', count: 0 },
        { name: '51-75%', count: 0 }, { name: '76-99%', count: 0 }, { name: '100%', count: 0 },
      ];
      pcts.forEach(p => {
        if (p <= 0) r[0].count++;
        else if (p <= 25) r[1].count++;
        else if (p <= 50) r[2].count++;
        else if (p <= 75) r[3].count++;
        else if (p < 100) r[4].count++;
        else r[5].count++;
      });
      return r;
    };
    let shopDistribution = [], fieldDistribution = [];
    let shopComplete = 0, fieldComplete = 0, shopNotStarted = 0, fieldNotStarted = 0, fabbedNotInstalled = 0;
    if (hasCreditSplit) {
      const shopPcts = data.map(rowShopPct);
      const fieldPcts = data.map(rowFieldPct);
      shopDistribution = buildDist(shopPcts);
      fieldDistribution = buildDist(fieldPcts);
      shopComplete = shopPcts.filter(p => p >= 99.5).length;
      fieldComplete = fieldPcts.filter(p => p >= 99.5).length;
      shopNotStarted = shopPcts.filter(p => p <= 0).length;
      fieldNotStarted = fieldPcts.filter(p => p <= 0).length;
      fabbedNotInstalled = data.filter(r => rowShopPct(r) >= 99.5 && rowFieldPct(r) < 99.5).length;
    }

    const onHoldItems = data.filter(row => row.onHold === true || row.on_hold === true);
    const onHoldBudgetHrs = onHoldItems.reduce((s, r) => s + getBudgetHrs(r), 0);
    const onHoldRemainingHrs = onHoldItems.reduce((s, r) => s + Math.max(0, getBudgetHrs(r) - getEarnedHrs(r)), 0);
    const onHoldCredit = onHoldItems.reduce((s, r) => s + getBudgetCredit(r), 0);
    let oldestHoldDays = 0;
    onHoldItems.forEach((r) => {
      const ts = r.holdUpdatedAt || r.hold_updated_at;
      if (ts) {
        const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
        if (days > oldestHoldDays) oldestHoldDays = days;
      }
    });

    return {
      totalRows, avgProgress, completed, inProgress, notStarted, byArea, byStatus,
      progressDistribution: ranges, onHoldItems, onHoldCount: onHoldItems.length,
      totalBudgetHrs, totalEarnedHrs, remainingHrs, hrsProgress,
      totalCredit, totalEarnedCredit, creditProgress, hasHoursData,
      totalEarnedCreditShop, totalEarnedCreditField, fabEligibleCredit, shopCreditProgress, fieldCreditProgress, hasCreditSplit,
      shopDistribution, fieldDistribution, shopComplete, fieldComplete, shopNotStarted, fieldNotStarted, fabbedNotInstalled,
      byAreaRemaining, almostDoneCount, almostDoneRemainingHrs,
      onHoldBudgetHrs, onHoldRemainingHrs, onHoldCredit, oldestHoldDays,
    };
  }, [srcData]);

  // Client-side SOV analytics (used by SOV tab + PDF/Excel export).
  const sovAnalytics = useMemo(() => {
    if (!srcData || srcData.length === 0) return { groups: [], hasData: false };

    const getSov = (row) => {
      const val = row.sovCode || row.sov_code || '';
      if (typeof val === 'string' && val.trim()) return val.trim();
      for (let i = 1; i <= 10; i++) {
        const uv = row[`udf${String(i).padStart(2, '0')}`];
        if (uv && typeof uv === 'string' && /^SOV[- ]?\d/i.test(uv.trim())) return uv.trim();
      }
      return '';
    };

    const hasAnySov = srcData.some(r => getSov(r) !== '');
    if (!hasAnySov) return { groups: [], hasData: false };

    const groupMap = {};
    srcData.forEach(row => {
      const sov = getSov(row) || 'Unassigned';
      if (!groupMap[sov]) groupMap[sov] = { sovCode: sov, totalBudgetHrs: 0, earnedHrs: 0, totalCredit: 0, fabEligibleCredit: 0, earnedCredit: 0, earnedCreditShop: 0, earnedCreditField: 0, count: 0 };
      groupMap[sov].count += 1;
      groupMap[sov].totalBudgetHrs += parseFloat(row.budgetedHr || row.budgeted_hr || 0) || 0;
      groupMap[sov].earnedHrs += parseFloat(row.earnedHr || row.earned_hr || 0) || 0;
      const credit = parseFloat(row.credit || 0) || 0;
      groupMap[sov].totalCredit += credit;
      if (!isFieldRunShop(row.fabricationShop || row.fabrication_shop)) groupMap[sov].fabEligibleCredit += credit;
      groupMap[sov].earnedCredit += parseFloat(row.earnedCredit || row.earned_credit || 0) || 0;
      groupMap[sov].earnedCreditShop += parseFloat(row.earnedCreditShop || row.earned_credit_shop || 0) || 0;
      groupMap[sov].earnedCreditField += parseFloat(row.earnedCreditField || row.earned_credit_field || 0) || 0;
    });

    const groups = Object.values(groupMap).map(g => ({
      ...g,
      hrsProgress: g.totalBudgetHrs > 0 ? Math.round((g.earnedHrs / g.totalBudgetHrs) * 1000) / 10 : 0,
      creditProgress: g.totalCredit > 0 ? Math.round((g.earnedCredit / g.totalCredit) * 1000) / 10 : 0,
      shopProgress: g.fabEligibleCredit > 0 ? Math.round((g.earnedCreditShop / g.fabEligibleCredit) * 1000) / 10 : 0,
      fieldProgress: g.totalCredit > 0 ? Math.round((g.earnedCreditField / g.totalCredit) * 1000) / 10 : 0,
    })).sort((a, b) => {
      if (a.sovCode === 'Unassigned') return 1;
      if (b.sovCode === 'Unassigned') return -1;
      return a.sovCode.localeCompare(b.sovCode);
    });

    return { groups, hasData: true };
  }, [srcData]);

  // Draws a row of summary "cards" across the page width, returns the Y below them.
  const drawPdfCards = useCallback((doc, cards, startY, pageW) => {
    const n = Math.max(1, cards.length);
    const cardY = startY + 1;
    const cardW = (pageW - 28 - (n - 1) * 4) / n;
    cards.forEach((c, i) => {
      const x = 14 + i * (cardW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, cardY, cardW, 16, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, cardY, 1.5, 16, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, cardY + 5);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(String(c.val), x + 4, cardY + 11);
      if (c.sub) { doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(148, 163, 184); doc.text(c.sub, x + 4, cardY + 14.5); }
    });
    return cardY + 20;
  }, []);

  // Overview PDF — works for every discipline (driven by the client-side `analytics`).
  const buildOverviewPdf = useCallback(async () => {
    const a = analytics;
    if (!a || a.totalRows === 0) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const fmtHrs = (v) => (v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
    const pct = (n, d) => d > 0 ? ((n / d) * 100).toFixed(1) + '%' : '0%';

    let startY = await addPdfHeader(doc);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Progress Overview`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const cards = [
      { label: 'Total Items', val: a.totalRows, color: [99, 102, 241] },
      { label: 'Completed', val: a.completed, sub: `${pct(a.completed, a.totalRows)} of items`, color: [16, 185, 129] },
      { label: 'In Progress', val: a.inProgress, color: [245, 158, 11] },
      { label: 'Not Started', val: a.notStarted, sub: `${pct(a.notStarted, a.totalRows)} at 0%`, color: [239, 68, 68] },
    ];
    if (a.hasHoursData) cards.push({ label: 'Earned Hours', val: `${a.hrsProgress.toFixed(1)}%`, sub: `${fmtHrs(a.totalEarnedHrs)} / ${fmtHrs(a.totalBudgetHrs)} hrs`, color: [59, 130, 246] });
    if (a.totalCredit > 0) {
      if (a.hasCreditSplit) {
        cards.push({ label: 'Shop (Fab) Credit', val: `${a.shopCreditProgress.toFixed(1)}%`, sub: `${fmtHrs(a.totalEarnedCreditShop)} / ${fmtHrs(a.totalCredit)}`, color: [245, 158, 11] });
        cards.push({ label: 'Field (Install) Credit', val: `${a.fieldCreditProgress.toFixed(1)}%`, sub: `${fmtHrs(a.totalEarnedCreditField)} / ${fmtHrs(a.totalCredit)}`, color: [16, 185, 129] });
      } else {
        cards.push({ label: 'Earned Credit', val: `${a.creditProgress.toFixed(1)}%`, sub: `${fmtHrs(a.totalEarnedCredit)} / ${fmtHrs(a.totalCredit)}`, color: [124, 58, 237] });
      }
    }
    if (a.onHoldCount > 0) cards.push({ label: 'On Hold', val: a.onHoldCount, sub: `${pct(a.onHoldCount, a.totalRows)} of items`, color: [220, 38, 38] });
    startY = drawPdfCards(doc, cards, startY, pageW);

    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(a.hasCreditSplit ? 'Fab vs Install Distribution' : 'Progress Distribution', 14, startY + 2);
    autoTable(doc, {
      startY: startY + 4,
      head: a.hasCreditSplit
        ? [['Progress Range', 'Fab Items', 'Install Items']]
        : [['Progress Range', 'Items', '% of Total']],
      body: a.hasCreditSplit
        ? (a.shopDistribution || []).map((r, i) => [r.name, String(r.count), String((a.fieldDistribution[i] || {}).count || 0)])
        : (a.progressDistribution || []).map(r => [r.name, String(r.count), pct(r.count, a.totalRows)]),
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
      tableWidth: (pageW - 28) / 2 - 4,
      margin: { left: 14 },
    });

    const areaHead = a.hasHoursData
      ? [['Area', 'Items', 'Avg %', 'Budget Hrs', 'Earned Hrs', 'Remaining Hrs']]
      : [['Area', 'Items', 'Avg %']];
    const areaBody = (a.byArea || []).map(ar => a.hasHoursData
      ? [ar.name, String(ar.count), `${(ar.avgProgress || 0).toFixed(1)}%`, fmtHrs(ar.budgetHrs), fmtHrs(ar.earnedHrs), fmtHrs(Math.max(0, (ar.budgetHrs || 0) - (ar.earnedHrs || 0)))]
      : [ar.name, String(ar.count), `${(ar.avgProgress || 0).toFixed(1)}%`]);
    autoTable(doc, {
      startY: startY + 4,
      head: areaHead,
      body: areaBody,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
      tableWidth: (pageW - 28) / 2 - 4,
      margin: { left: pageW / 2 + 4 },
    });
    return doc;
  }, [analytics, disciplineName, addPdfHeader, drawPdfCards]);

  // SOV PDF — works for every discipline (driven by client-side `sovAnalytics`).
  const buildSovPdf = useCallback(async () => {
    if (!sovAnalytics.hasData) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { groups } = sovAnalytics;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const f = (v) => (parseFloat(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
    const pctS = (a, b) => { const bv = parseFloat(b) || 0; return bv > 0 ? ((parseFloat(a) / bv) * 100).toFixed(1) + '%' : '0%'; };

    let startY = await addPdfHeader(doc);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    const showHours = sovViewMode === 'internal';
    doc.text(`${disciplineName} - SOV Summary`, pageW / 2, startY + 2, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(showHours ? 'Internal view (hours + credits)' : 'Client view (credits)', pageW / 2, startY + 7, { align: 'center' });
    startY += 10;

    const totItems = groups.reduce((s, g) => s + g.count, 0);
    const totBh = groups.reduce((s, g) => s + g.totalBudgetHrs, 0);
    const totEh = groups.reduce((s, g) => s + g.earnedHrs, 0);
    const totC = groups.reduce((s, g) => s + g.totalCredit, 0);
    const totEc = groups.reduce((s, g) => s + g.earnedCredit, 0);
    const totEcShop = groups.reduce((s, g) => s + g.earnedCreditShop, 0);
    const totEcField = groups.reduce((s, g) => s + g.earnedCreditField, 0);
    startY = drawPdfCards(doc, [
      { label: 'SOV Codes', val: groups.filter(g => g.sovCode !== 'Unassigned').length, color: [99, 102, 241] },
      { label: 'Total Items', val: totItems, color: [59, 130, 246] },
      ...(showHours ? [
        { label: 'Budget Hrs', val: f(totBh), color: [30, 64, 175] },
        { label: 'Earned Hrs', val: f(totEh), sub: `${pctS(totEh, totBh)} of budget`, color: [16, 185, 129] },
      ] : []),
      { label: 'Total Credit', val: f(totC), color: [124, 58, 237] },
      ...(isPiping
        ? [
            { label: 'Shop (Fab) Credit', val: f(totEcShop), sub: `${pctS(totEcShop, groups.reduce((s, g) => s + (g.fabEligibleCredit || 0), 0) || totC)} of shop credit`, color: [245, 158, 11] },
            { label: 'Field (Install) Credit', val: f(totEcField), sub: `${pctS(totEcField, totC)} of credit`, color: [5, 150, 105] },
          ]
        : [{ label: 'Earned Credit', val: f(totEc), sub: `${pctS(totEc, totC)} of total`, color: [5, 150, 105] }]),
    ], startY, pageW);

    const hourCells = (g) => (showHours ? [f(g.totalBudgetHrs), f(g.earnedHrs), `${g.hrsProgress}%`] : []);
    const hourTotals = showHours ? [f(totBh), f(totEh), pctS(totEh, totBh)] : [];
    const hourHead = showHours ? ['Total Hrs', 'Earned Hrs', 'Hrs %'] : [];
    const body = groups.map(g => [
      g.sovCode, String(g.count), ...hourCells(g),
      f(g.totalCredit),
      ...(isPiping
        ? [f(g.earnedCreditShop), `${g.shopProgress}%`, f(g.earnedCreditField), `${g.fieldProgress}%`]
        : [f(g.earnedCredit), `${g.creditProgress}%`]),
    ]);
    body.push([
      { content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[String(totItems), ...hourTotals, f(totC),
        ...(isPiping
          ? [f(totEcShop), pctS(totEcShop, groups.reduce((s, g) => s + (g.fabEligibleCredit || 0), 0) || totC), f(totEcField), pctS(totEcField, totC)]
          : [f(totEc), pctS(totEc, totC)])]
        .map(v => ({ content: v, styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } })),
    ]);
    autoTable(doc, {
      startY,
      head: [['SOV Code', 'Items', ...hourHead, 'Total Credit',
        ...(isPiping ? ['Shop (Fab)', 'Shop %', 'Field (Install)', 'Field %'] : ['Earned Credit', 'Credit %'])]],
      body,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [sovAnalytics, disciplineName, addPdfHeader, drawPdfCards, isPiping, sovViewMode]);

  // On-Hold PDF — works for every discipline (driven by client-side `analytics`).
  const buildOnHoldPdf = useCallback(async () => {
    const items = analytics.onHoldItems || [];
    if (items.length === 0) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();

    let startY = await addPdfHeader(doc);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Items On Hold (${items.length})`, pageW / 2, startY + 2, { align: 'center' });
    startY += 8;

    const body = items.map(item => [
      item.isoSheetNumber || item.iso_sheet_number || '—',
      item.fluidCode || item.fluid_code || '—',
      item.pAndId || item.pnid || '—',
      item.size1 || item.size_1 || '—',
      item.material1 || item.material_1 || '—',
      item.comments || '—',
      item.holdUpdatedBy || item.hold_updated_by || '—',
      formatHoldDate(item.holdUpdatedAt || item.hold_updated_at),
    ]);
    autoTable(doc, {
      startY,
      head: [['ISO Sheet', 'Fluid Code', 'P&ID', 'Size', 'Material', 'Comments', 'Put on Hold By', 'Hold Date']],
      body,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'left', overflow: 'linebreak' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold', halign: 'left' },
      columnStyles: { 5: { cellWidth: 60 } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [analytics, disciplineName, addPdfHeader]);

  // Shop Summary PDF (piping) — driven by the fetched `shopSummaryData`.
  const buildShopSummaryPdf = useCallback(async () => {
    if (!shopSummaryData || !shopSummaryData.rows || shopSummaryData.rows.length === 0) return null;
    const { jsPDF, autoTable } = await loadPdfLibs();
    const { rows, summary } = shopSummaryData;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const f = (v) => Math.round(parseFloat(v) || 0).toLocaleString('en-US');

    let startY = await addPdfHeader(doc);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(`${disciplineName} - Shop Summary`, pageW / 2, startY + 2, { align: 'center' });
    startY += 6;

    const gs = summary;
    const totalFt = (parseFloat(gs.total_sb_ft) || 0) + (parseFloat(gs.total_lb_ft) || 0);
    const fabFt = (parseFloat(gs.fab_sb_ft) || 0) + (parseFloat(gs.fab_lb_ft) || 0);
    const fabPct = totalFt > 0 ? ((fabFt / totalFt) * 100).toFixed(1) + '%' : '0%';
    startY = drawPdfCards(doc, [
      { label: 'Fab Shops', val: parseInt(gs.shop_count) || 0, color: [99, 102, 241] },
      { label: 'Total ISOs', val: parseInt(gs.total_isos) || 0, color: [59, 130, 246] },
      { label: 'Total Footage', val: f(totalFt) + ' ft', color: [30, 64, 175] },
      { label: 'Fabricated', val: f(fabFt) + ' ft', sub: `${fabPct} of total`, color: [245, 158, 11] },
      { label: 'Shipped', val: parseInt(gs.shipped_count) || 0, color: [15, 118, 110] },
      { label: 'Received', val: parseInt(gs.received_count) || 0, color: [16, 185, 129] },
    ], startY, pageW);

    const body = rows.map(row => {
      const rTot = (parseFloat(row.total_sb_ft) || 0) + (parseFloat(row.total_lb_ft) || 0);
      const rFab = (parseFloat(row.fab_sb_ft) || 0) + (parseFloat(row.fab_lb_ft) || 0);
      const rPct = rTot > 0 ? (Math.round((rFab / rTot) * 1000) / 10) + '%' : '0%';
      return [row.shop, parseInt(row.total_isos) || 0, f(rTot), f(rFab), f(rTot - rFab), rPct,
        parseInt(row.shop_complete_count) || 0, parseInt(row.shop_in_progress_count) || 0,
        parseInt(row.shop_not_started_count) || 0, parseInt(row.shipped_count) || 0, parseInt(row.received_count) || 0];
    });
    body.push([
      { content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[parseInt(gs.total_isos) || 0, f(totalFt), f(fabFt), f(totalFt - fabFt), fabPct,
        parseInt(gs.shop_complete_count) || 0,
        (parseInt(gs.total_isos) || 0) - (parseInt(gs.shop_complete_count) || 0) - rows.reduce((s, r) => s + (parseInt(r.shop_not_started_count) || 0), 0),
        rows.reduce((s, r) => s + (parseInt(r.shop_not_started_count) || 0), 0),
        parseInt(gs.shipped_count) || 0, parseInt(gs.received_count) || 0]
        .map(v => ({ content: String(v), styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } })),
    ]);
    autoTable(doc, {
      startY,
      head: [['Fabrication Shop', 'ISOs', 'Total FT', 'Fab FT', 'Remaining FT', 'Fab %', 'Complete', 'In Progress', 'Not Started', 'Shipped', 'Received']],
      body,
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    });
    return doc;
  }, [shopSummaryData, disciplineName, addPdfHeader, drawPdfCards]);

  // Resolves the export to the nearest parent-owned dataset for embedded piping
  // sub-views (which fetch their own data and aren't directly exportable here).
  const resolveExportView = useCallback(() => {
    if (selectedView === 'sovAnalytics' || selectedView === 'sovWhatIf') return 'sov';
    if (selectedView === 'shopTabs') return shopSummaryData ? 'shopSummary' : 'overview';
    return selectedView;
  }, [selectedView, shopSummaryData]);

  // Builds the right PDF for the current view. Always returns a doc (falls back to
  // Overview) so the Download button never silently does nothing.
  const buildPdfForView = useCallback(async () => {
    const view = resolveExportView();
    let doc = null;
    if (view === 'quantity' && isPiping) doc = await buildQuantityPdf();
    else if (view === 'quantity2' && isPiping) doc = await buildQuantity2Pdf();
    else if (view === 'quantity3' && isPiping) doc = await buildQuantity3Pdf();
    else if (view === 'quantity4' && isPiping) doc = await buildQuantity4Pdf();
    else if (view === 'material' && isPiping) doc = await buildMaterialPdf();
    else if (view === 'shopSummary' && isPiping) doc = await buildShopSummaryPdf();
    else if (view === 'service') doc = await buildServicePdf();
    else if (view === 'sov') doc = await buildSovPdf();
    else if (view === 'onHold') doc = await buildOnHoldPdf();
    else if (view === 'overview') doc = await buildOverviewPdf();
    if (!doc) doc = await buildOverviewPdf();
    return { doc, view };
  }, [resolveExportView, isPiping, buildQuantityPdf, buildQuantity2Pdf, buildQuantity3Pdf, buildQuantity4Pdf, buildMaterialPdf, buildShopSummaryPdf, buildServicePdf, buildSovPdf, buildOnHoldPdf, buildOverviewPdf]);

  const handleDownloadPdf = useCallback(async () => {
    const { doc, view } = await buildPdfForView();
    if (doc) doc.save(exportFilename({ job: projectDetails?.job_number || projectId, reportSlug: `${disciplineId}_${view}`, ext: 'pdf' }));
  }, [buildPdfForView, disciplineId]);

  const handleDownloadExcel = useCallback(async () => {
    const ExcelJS = await loadExcelLib();
    const wb = new ExcelJS.Workbook();
    wb.creator = 'LGL Tools';
    wb.created = new Date();

    const jobLabel = projectDetails?.job_number
      ? `${projectDetails.job_number}${projectDetails.job_name ? ' - ' + projectDetails.job_name : ''}`
      : '';
    const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
    const HEADER_ARGB = 'FF1E3A5F';
    const TOTAL_ARGB = 'FF1E293B';

    // Adds a title/subtitle/date block; returns the next free row index.
    const titleBlock = (ws, title, span) => {
      ws.mergeCells(1, 1, 1, span);
      const t = ws.getCell(1, 1);
      t.value = title;
      t.font = { bold: true, size: 14, color: { argb: HEADER_ARGB } };
      let row = 2;
      if (jobLabel) {
        ws.mergeCells(row, 1, row, span);
        const j = ws.getCell(row, 1);
        j.value = jobLabel;
        j.font = { size: 10, color: { argb: 'FF475569' } };
        row += 1;
      }
      ws.mergeCells(row, 1, row, span);
      const d = ws.getCell(row, 1);
      d.value = `Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
      d.font = { size: 9, italic: true, color: { argb: 'FF94A3B8' } };
      return row + 2;
    };

    // Writes a header row + body rows + optional total row starting at `startRow`.
    // columns: [{ header, width, numFmt }]; rows: array of value arrays.
    const writeTable = (ws, startRow, columns, rows, totalRow) => {
      const headerRow = ws.getRow(startRow);
      columns.forEach((col, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = col.header;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_ARGB } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.alignment = { horizontal: i === 0 ? 'left' : 'center', vertical: 'middle', wrapText: true };
      });
      headerRow.commit();
      let r = startRow + 1;
      rows.forEach((rowVals) => {
        const row = ws.getRow(r);
        rowVals.forEach((v, i) => {
          const cell = row.getCell(i + 1);
          cell.value = v;
          cell.alignment = { horizontal: i === 0 ? 'left' : 'center' };
          if (columns[i] && columns[i].numFmt && typeof v === 'number') cell.numFmt = columns[i].numFmt;
        });
        row.commit();
        r += 1;
      });
      if (totalRow) {
        const row = ws.getRow(r);
        totalRow.forEach((v, i) => {
          const cell = row.getCell(i + 1);
          cell.value = v;
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_ARGB } };
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.alignment = { horizontal: i === 0 ? 'left' : 'center' };
          if (columns[i] && columns[i].numFmt && typeof v === 'number') cell.numFmt = columns[i].numFmt;
        });
        row.commit();
        r += 1;
      }
      columns.forEach((col, i) => { ws.getColumn(i + 1).width = col.width || 16; });
      return r;
    };

    const view = resolveExportView();
    const FT = '#,##0';
    const CR = '#,##0.0';

    if (view === 'overview') {
      const a = analytics;
      const ws = wb.addWorksheet('Overview');
      let row = titleBlock(ws, `${disciplineName} - Progress Overview`, 6);
      const pctN = (n, d) => d > 0 ? Math.round((n / d) * 1000) / 10 : 0;
      const summary = [
        ['Total Items', a.totalRows], ['Completed', a.completed], ['In Progress', a.inProgress], ['Not Started', a.notStarted],
      ];
      if (a.hasHoursData) { summary.push(['Budget Hours', Math.round(a.totalBudgetHrs)]); summary.push(['Earned Hours', Math.round(a.totalEarnedHrs)]); summary.push(['Remaining Hours', Math.round(a.remainingHrs)]); }
      if (a.totalCredit > 0) {
        summary.push(['Total Credit', Math.round(a.totalCredit * 10) / 10]);
        if (a.hasCreditSplit) {
          summary.push(['Shop (Fab) Credit', Math.round(a.totalEarnedCreditShop * 10) / 10]);
          summary.push(['Shop (Fab) %', Math.round(a.shopCreditProgress * 10) / 10]);
          summary.push(['Field (Install) Credit', Math.round(a.totalEarnedCreditField * 10) / 10]);
          summary.push(['Field (Install) %', Math.round(a.fieldCreditProgress * 10) / 10]);
        } else {
          summary.push(['Earned Credit', Math.round(a.totalEarnedCredit * 10) / 10]);
        }
      }
      summary.push(['On Hold', a.onHoldCount]);
      row = writeTable(ws, row, [{ header: 'Metric', width: 22 }, { header: 'Value', width: 18, numFmt: FT }], summary);
      row += 1;
      row = a.hasCreditSplit
        ? writeTable(ws, row,
          [{ header: 'Progress Range', width: 18 }, { header: 'Fab Items', width: 12, numFmt: FT }, { header: 'Install Items', width: 12, numFmt: FT }],
          (a.shopDistribution || []).map((d, i) => [d.name, d.count, (a.fieldDistribution[i] || {}).count || 0]))
        : writeTable(ws, row,
          [{ header: 'Progress Range', width: 18 }, { header: 'Items', width: 12, numFmt: FT }, { header: '% of Total', width: 12, numFmt: '0.0"%"' }],
          (a.progressDistribution || []).map(d => [d.name, d.count, pctN(d.count, a.totalRows)]));
      row += 1;
      const areaCols = a.hasHoursData
        ? [{ header: 'Area', width: 26 }, { header: 'Items', width: 10, numFmt: FT }, { header: 'Avg %', width: 10, numFmt: '0.0"%"' }, { header: 'Budget Hrs', width: 14, numFmt: FT }, { header: 'Earned Hrs', width: 14, numFmt: FT }, { header: 'Remaining Hrs', width: 14, numFmt: FT }]
        : [{ header: 'Area', width: 26 }, { header: 'Items', width: 10, numFmt: FT }, { header: 'Avg %', width: 10, numFmt: '0.0"%"' }];
      writeTable(ws, row, areaCols, (a.byArea || []).map(ar => a.hasHoursData
        ? [ar.name, ar.count, Math.round((ar.avgProgress || 0) * 10) / 10, Math.round(ar.budgetHrs || 0), Math.round(ar.earnedHrs || 0), Math.round(Math.max(0, (ar.budgetHrs || 0) - (ar.earnedHrs || 0)))]
        : [ar.name, ar.count, Math.round((ar.avgProgress || 0) * 10) / 10]));
    } else if (view === 'service' && serviceAnalytics.hasData) {
      const { sections, totals, banded } = serviceAnalytics;
      const ws = wb.addWorksheet('Service');
      let row = titleBlock(ws, `${disciplineName} - Service (Credit Summary, by ${serviceGroupLabel(serviceGroupBy1)} / ${serviceGroupLabel(serviceGroupBy2)})`, 8);
      const pctN = (n, d) => d > 0 ? Math.round((n / d) * 1000) / 10 : 0;
      const cols = [
        { header: serviceGroupLabel(serviceGroupBy2), width: 22 }, { header: 'Items', width: 10, numFmt: FT }, { header: 'Total Credit', width: 14, numFmt: CR },
        { header: 'Fab Earned', width: 14, numFmt: CR }, { header: 'Fab %', width: 10, numFmt: '0.0"%"' },
        { header: 'Install Earned', width: 14, numFmt: CR }, { header: 'Install %', width: 10, numFmt: '0.0"%"' }, { header: 'Remaining', width: 14, numFmt: CR },
      ];
      const rowVals = (g) => [g.items, num(g.totalCredit), num(g.fabCredit), pctN(g.fabCredit, g.totalCredit), num(g.instCredit), pctN(g.instCredit, g.totalCredit), num(g.totalCredit - g.instCredit)];
      const body = [];
      const bandRowIdx = [];
      const subtotalRowIdx = [];
      sections.forEach(section => {
        if (banded) { bandRowIdx.push(body.length); body.push([section.label]); }
        section.groups.forEach(g => body.push([g.label, ...rowVals(g)]));
        if (banded) { subtotalRowIdx.push(body.length); body.push([`${section.label} - Subtotal`, ...rowVals(section.totals)]); }
      });
      writeTable(ws, row, cols, body, ['Grand Total', ...rowVals(totals)]);
      bandRowIdx.forEach(i => {
        const r = ws.getRow(row + 1 + i);
        for (let c = 1; c <= cols.length; c++) {
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        }
        r.getCell(1).font = { bold: true, color: { argb: 'FFB45309' } };
      });
      subtotalRowIdx.forEach(i => {
        const r = ws.getRow(row + 1 + i);
        for (let c = 1; c <= cols.length; c++) {
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8ECF4' } };
          r.getCell(c).font = { bold: true };
        }
      });
    } else if (view === 'sov' && sovAnalytics.hasData) {
      const { groups } = sovAnalytics;
      const showHours = sovViewMode === 'internal';
      const ws = wb.addWorksheet('SOV');
      const totItems = groups.reduce((s, g) => s + g.count, 0);
      const totBh = groups.reduce((s, g) => s + g.totalBudgetHrs, 0);
      const totEh = groups.reduce((s, g) => s + g.earnedHrs, 0);
      const totC = groups.reduce((s, g) => s + g.totalCredit, 0);
      const totEc = groups.reduce((s, g) => s + g.earnedCredit, 0);
      const totEcShop = groups.reduce((s, g) => s + g.earnedCreditShop, 0);
      const totEcField = groups.reduce((s, g) => s + g.earnedCreditField, 0);
      const pctN = (n, d) => d > 0 ? Math.round((n / d) * 1000) / 10 : 0;
      const hourCols = showHours ? [
        { header: 'Total Hrs', width: 14, numFmt: CR },
        { header: 'Earned Hrs', width: 14, numFmt: CR },
        { header: 'Hrs %', width: 10, numFmt: '0.0"%"' },
      ] : [];
      const cols = [
        { header: 'SOV Code', width: 18 }, { header: 'Items', width: 10, numFmt: FT },
        ...hourCols,
        { header: 'Total Credit', width: 14, numFmt: CR },
        ...(isPiping ? [
          { header: 'Shop (Fab)', width: 14, numFmt: CR }, { header: 'Shop %', width: 10, numFmt: '0.0"%"' },
          { header: 'Field (Install)', width: 16, numFmt: CR }, { header: 'Field %', width: 10, numFmt: '0.0"%"' },
        ] : [
          { header: 'Earned Credit', width: 14, numFmt: CR }, { header: 'Credit %', width: 10, numFmt: '0.0"%"' },
        ]),
      ];
      const hourCells = (g) => (showHours ? [num(g.totalBudgetHrs), num(g.earnedHrs), g.hrsProgress] : []);
      const hourTotals = showHours ? [num(totBh), num(totEh), pctN(totEh, totBh)] : [];
      let row = titleBlock(ws, `${disciplineName} - SOV Summary (${showHours ? 'Internal — Hours + Credits' : 'Client — Credits'})`, cols.length);
      writeTable(ws, row, cols,
        groups.map(g => [g.sovCode, g.count, ...hourCells(g), num(g.totalCredit),
          ...(isPiping ? [num(g.earnedCreditShop), g.shopProgress, num(g.earnedCreditField), g.fieldProgress] : [num(g.earnedCredit), g.creditProgress])]),
        ['Grand Total', totItems, ...hourTotals, num(totC),
          ...(isPiping ? [num(totEcShop), pctN(totEcShop, groups.reduce((s, g) => s + (g.fabEligibleCredit || 0), 0) || totC), num(totEcField), pctN(totEcField, totC)] : [num(totEc), pctN(totEc, totC)])]);
    } else if (view === 'onHold' && (analytics.onHoldItems || []).length > 0) {
      const items = analytics.onHoldItems;
      const ws = wb.addWorksheet('On Hold');
      let row = titleBlock(ws, `${disciplineName} - Items On Hold (${items.length})`, 8);
      const cols = [
        { header: 'ISO Sheet', width: 18 }, { header: 'Fluid Code', width: 14 }, { header: 'P&ID', width: 14 }, { header: 'Size', width: 10 },
        { header: 'Material', width: 16 }, { header: 'Comments', width: 40 }, { header: 'Put on Hold By', width: 18 }, { header: 'Hold Date', width: 16 },
      ];
      writeTable(ws, row, cols, items.map(item => [
        item.isoSheetNumber || item.iso_sheet_number || '', item.fluidCode || item.fluid_code || '', item.pAndId || item.pnid || '',
        item.size1 || item.size_1 || '', item.material1 || item.material_1 || '', item.comments || '',
        item.holdUpdatedBy || item.hold_updated_by || '', formatHoldDate(item.holdUpdatedAt || item.hold_updated_at),
      ]));
    } else if ((view === 'quantity' || view === 'quantity2' || view === 'quantity3' || view === 'quantity4') && quantityDataFor(view)) {
      const qd = quantityDataFor(view);
      const isTransport = view === 'quantity2' || view === 'quantity4';
      const isGrouped = view === 'quantity3' || view === 'quantity4';
      const groupBy = view === 'quantity3' ? quantity3GroupBy : quantity4GroupBy;
      const groupLabel = (QUANTITY_GROUP_OPTIONS.find(o => o.id === groupBy) || {}).label || 'Group';
      const sheetNames = { quantity: 'Quantity', quantity2: 'Quantity 2', quantity3: 'Quantity 3', quantity4: 'Quantity 4' };
      const ws = wb.addWorksheet(sheetNames[view]);
      let row = titleBlock(ws, `${disciplineName} - Piping Quantity Summary${isTransport ? ' (Transport Install)' : ''}${isGrouped ? ` (by ${groupLabel})` : ''}`, 10);
      const cols = [
        { header: isGrouped ? `${groupLabel} / Fluid Code` : 'Fluid Code', width: 22 }, { header: 'ISOs', width: 10, numFmt: FT },
        { header: 'Total FT (SB)', width: 14, numFmt: FT }, { header: 'Total FT (LB)', width: 14, numFmt: FT },
        { header: 'Fab FT (SB)', width: 14, numFmt: FT }, { header: 'Fab FT (LB)', width: 14, numFmt: FT },
        { header: 'Inst FT (SB)', width: 14, numFmt: FT }, { header: 'Inst FT (LB)', width: 14, numFmt: FT },
        { header: 'Rem FT (SB)', width: 14, numFmt: FT }, { header: 'Rem FT (LB)', width: 14, numFmt: FT },
      ];
      // Sections: Field Run/rest for Quantity 1-2, one section per group key for 3-4.
      let sections;
      if (isGrouped) {
        const groupsMap = {};
        qd.rows.forEach(r => { const k = r.group_key || 'Unspecified'; (groupsMap[k] = groupsMap[k] || []).push(r); });
        sections = Object.keys(groupsMap).map(k => ({ label: k, rows: groupsMap[k], subtotalSuffix: 'Total' }));
      } else {
        sections = [
          { label: 'Field Run', rows: qd.rows.filter(r => r.is_field_run), subtotalSuffix: 'Subtotal' },
          { label: 'Shop Fabricated', rows: qd.rows.filter(r => !r.is_field_run), subtotalSuffix: 'Subtotal' },
        ].filter(s => s.rows.length > 0);
      }
      const showBands = isGrouped || sections.length > 1;
      const body = [];
      const bandRowIdx = [];
      const subtotalRowIdx = [];
      sections.forEach(section => {
        if (showBands) { bandRowIdx.push(body.length); body.push([section.label]); }
        section.rows.forEach(r => {
          const tsb = num(r.total_sb_ft), tlb = num(r.total_lb_ft), isb = num(r.installed_sb_ft), ilb = num(r.installed_lb_ft);
          body.push([r.fluid_code, num(r.iso_count), tsb, tlb, num(r.fab_sb_ft), num(r.fab_lb_ft), isb, ilb, tsb - isb, tlb - ilb]);
        });
        if (showBands) {
          const t = section.rows.reduce((a, r) => ({
            isos: a.isos + num(r.iso_count), tsb: a.tsb + num(r.total_sb_ft), tlb: a.tlb + num(r.total_lb_ft),
            fsb: a.fsb + num(r.fab_sb_ft), flb: a.flb + num(r.fab_lb_ft),
            isb: a.isb + num(r.installed_sb_ft), ilb: a.ilb + num(r.installed_lb_ft),
          }), { isos: 0, tsb: 0, tlb: 0, fsb: 0, flb: 0, isb: 0, ilb: 0 });
          subtotalRowIdx.push(body.length);
          body.push([`${section.label} - ${section.subtotalSuffix}`, t.isos, t.tsb, t.tlb, t.fsb, t.flb, t.isb, t.ilb, t.tsb - t.isb, t.tlb - t.ilb]);
        }
      });
      const gs = qd.summary;
      const gTsb = num(gs.total_sb_ft), gTlb = num(gs.total_lb_ft), gIsb = num(gs.installed_sb_ft), gIlb = num(gs.installed_lb_ft);
      writeTable(ws, row, cols, body,
        ['Grand Total', num(gs.total_isos), gTsb, gTlb, num(gs.fab_sb_ft), num(gs.fab_lb_ft), gIsb, gIlb, gTsb - gIsb, gTlb - gIlb]);
      // Style the band and subtotal rows (writeTable writes them as plain rows).
      bandRowIdx.forEach(i => {
        const r = ws.getRow(row + 1 + i);
        for (let c = 1; c <= cols.length; c++) {
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        }
        r.getCell(1).font = { bold: true, color: { argb: 'FFB45309' } };
      });
      subtotalRowIdx.forEach(i => {
        const r = ws.getRow(row + 1 + i);
        for (let c = 1; c <= cols.length; c++) {
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8ECF4' } };
          r.getCell(c).font = { bold: true };
        }
      });
    } else if (view === 'material' && materialData && materialData.rows) {
      const ws = wb.addWorksheet('Material');
      let row = titleBlock(ws, `${disciplineName} - Piping Material Summary (by Shop)`, 10);
      const cols = [
        { header: 'Material', width: 20 }, { header: 'ISOs', width: 10, numFmt: FT },
        { header: 'Total FT (SB)', width: 14, numFmt: FT }, { header: 'Total FT (LB)', width: 14, numFmt: FT },
        { header: 'Fab FT (SB)', width: 14, numFmt: FT }, { header: 'Fab FT (LB)', width: 14, numFmt: FT },
        { header: 'Inst FT (SB)', width: 14, numFmt: FT }, { header: 'Inst FT (LB)', width: 14, numFmt: FT },
        { header: 'Rem FT (SB)', width: 14, numFmt: FT }, { header: 'Rem FT (LB)', width: 14, numFmt: FT },
      ];
      const body = [];
      const shopGroups = {};
      materialData.rows.forEach(r => { (shopGroups[r.shop] = shopGroups[r.shop] || []).push(r); });
      Object.keys(shopGroups).forEach(shop => {
        shopGroups[shop].forEach(r => {
          const tsb = num(r.total_sb_ft), tlb = num(r.total_lb_ft), isb = num(r.installed_sb_ft), ilb = num(r.installed_lb_ft);
          body.push([r.material, num(r.iso_count), tsb, tlb, num(r.fab_sb_ft), num(r.fab_lb_ft), isb, ilb, tsb - isb, tlb - ilb]);
        });
      });
      const gs = materialData.summary;
      const gTsb = num(gs.total_sb_ft), gTlb = num(gs.total_lb_ft), gIsb = num(gs.installed_sb_ft), gIlb = num(gs.installed_lb_ft);
      writeTable(ws, row, cols, body,
        ['Grand Total', num(gs.total_isos), gTsb, gTlb, num(gs.fab_sb_ft), num(gs.fab_lb_ft), gIsb, gIlb, gTsb - gIsb, gTlb - gIlb]);
    } else if (view === 'shopSummary' && shopSummaryData && shopSummaryData.rows) {
      const { rows, summary: gs } = shopSummaryData;
      const ws = wb.addWorksheet('Shop Summary');
      let row = titleBlock(ws, `${disciplineName} - Shop Summary`, 11);
      const cols = [
        { header: 'Fabrication Shop', width: 22 }, { header: 'ISOs', width: 10, numFmt: FT }, { header: 'Total FT', width: 14, numFmt: FT },
        { header: 'Fab FT', width: 14, numFmt: FT }, { header: 'Remaining FT', width: 14, numFmt: FT }, { header: 'Fab %', width: 10, numFmt: '0.0"%"' },
        { header: 'Complete', width: 12, numFmt: FT }, { header: 'In Progress', width: 12, numFmt: FT }, { header: 'Not Started', width: 12, numFmt: FT },
        { header: 'Shipped', width: 12, numFmt: FT }, { header: 'Received', width: 12, numFmt: FT },
      ];
      const body = rows.map(r => {
        const tot = num(r.total_sb_ft) + num(r.total_lb_ft);
        const fab = num(r.fab_sb_ft) + num(r.fab_lb_ft);
        return [r.shop, num(r.total_isos), tot, fab, tot - fab, tot > 0 ? Math.round((fab / tot) * 1000) / 10 : 0,
          num(r.shop_complete_count), num(r.shop_in_progress_count), num(r.shop_not_started_count), num(r.shipped_count), num(r.received_count)];
      });
      const totFt = num(gs.total_sb_ft) + num(gs.total_lb_ft);
      const fabFt = num(gs.fab_sb_ft) + num(gs.fab_lb_ft);
      const notStarted = rows.reduce((s, r) => s + num(r.shop_not_started_count), 0);
      writeTable(ws, row, cols, body,
        ['Grand Total', num(gs.total_isos), totFt, fabFt, totFt - fabFt, totFt > 0 ? Math.round((fabFt / totFt) * 1000) / 10 : 0,
          num(gs.shop_complete_count), num(gs.total_isos) - num(gs.shop_complete_count) - notStarted, notStarted, num(gs.shipped_count), num(gs.received_count)]);
    } else {
      // Fallback: at least export the overview so the button always produces a file.
      const a = analytics;
      const ws = wb.addWorksheet('Overview');
      let row = titleBlock(ws, `${disciplineName} - Progress Overview`, 3);
      writeTable(ws, row, [{ header: 'Metric', width: 22 }, { header: 'Value', width: 18, numFmt: FT }],
        [['Total Items', a.totalRows], ['Completed', a.completed], ['In Progress', a.inProgress], ['Not Started', a.notStarted], ['On Hold', a.onHoldCount]]);
    }

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportFilename({ job: projectDetails?.job_number || projectId, reportSlug: `${disciplineId}_${view}`, ext: 'xlsx' });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    function quantityDataFor(v) {
      if (v === 'quantity2') return quantity2Data;
      if (v === 'quantity3') return quantity3Data;
      if (v === 'quantity4') return quantity4Data;
      return quantityData;
    }
  }, [resolveExportView, analytics, serviceAnalytics, serviceGroupBy1, serviceGroupBy2, sovAnalytics, sovViewMode, quantityData, quantity2Data, quantity3Data, quantity4Data, quantity3GroupBy, quantity4GroupBy, materialData, shopSummaryData, disciplineName, disciplineId, projectDetails, isPiping]);

  const handleSendEmail = useCallback(async () => {
    if (!emailAddresses.trim()) return;
    const { doc, view } = await buildPdfForView();
    const filename = `${disciplineId}_${view}_${new Date().toISOString().slice(0, 10)}.pdf`;
    const subject = `${disciplineName} Analytics Report (${view})`;
    if (!doc) return;
    setEmailSending(true);
    setEmailResult(null);
    try {
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const recipients = emailAddresses.split(/[,;\s]+/).filter(e => e.includes('@'));
      if (recipients.length === 0) { setEmailResult({ type: 'error', message: 'No valid email addresses' }); setEmailSending(false); return; }
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject,
        htmlBody: `<p>Please find attached the ${disciplineName} analytics report.</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename, fileblob: pdfBase64 }],
        feature: 'analytics_report',
      });
      if (response.data.success) {
        setEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s)` });
        setTimeout(() => { setShowEmailModal(false); setEmailResult(null); setEmailAddresses(''); }, 2000);
      } else {
        setEmailResult({ type: 'error', message: response.data.error || 'Failed to send' });
      }
    } catch (error) {
      setEmailResult({ type: 'error', message: error.response?.data?.error || 'Failed to send email' });
    } finally {
      setEmailSending(false);
    }
  }, [emailAddresses, buildPdfForView, instance, accounts, disciplineName, disciplineId]);

  const fetchQuantityAnalytics = useCallback(async () => {
    if (!projectId) return;
    setQuantityLoading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/quantity-analytics${asOfQuery}`);
      if (response.data.success) {
        setQuantityData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching quantity analytics:', error);
    } finally {
      setQuantityLoading(false);
    }
  }, [projectId, asOfQuery, instance, accounts]);

  const fetchMaterialAnalytics = useCallback(async () => {
    if (!projectId) return;
    setMaterialLoading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/material-analytics${asOfQuery}`);
      if (response.data.success) {
        setMaterialData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching material analytics:', error);
    } finally {
      setMaterialLoading(false);
    }
  }, [projectId, asOfQuery, instance, accounts]);

  const fetchShopSummaryAnalytics = useCallback(async () => {
    if (!projectId) return;
    setShopSummaryLoading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/shop-summary-analytics${asOfQuery}`);
      if (response.data.success) {
        setShopSummaryData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching shop summary analytics:', error);
    } finally {
      setShopSummaryLoading(false);
    }
  }, [projectId, asOfQuery, instance, accounts]);

  const fetchAuditLog = useCallback(async () => {
    if (!projectId) return;
    setAuditLogLoading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/material-audit-log`);
      if (response.data.success) {
        setAuditLogData(response.data.data.rows);
      }
    } catch (error) {
      console.error('Error fetching audit log:', error);
    } finally {
      setAuditLogLoading(false);
    }
  }, [projectId, instance, accounts]);

  const handleOpenAuditLog = useCallback(() => {
    setShowAuditLog(true);
    fetchAuditLog();
  }, [fetchAuditLog]);

  const fetchQuantity2Analytics = useCallback(async () => {
    if (!projectId) return;
    setQuantity2Loading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/quantity2-analytics${asOfQuery}`);
      if (response.data.success) {
        setQuantity2Data(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching quantity2 analytics:', error);
    } finally {
      setQuantity2Loading(false);
    }
  }, [projectId, instance, accounts, asOfQuery]);

  const fetchQuantity3Analytics = useCallback(async () => {
    if (!projectId) return;
    setQuantity3Loading(true);
    try {
      const qs = new URLSearchParams({ groupBy: quantity3GroupBy });
      if (asOfDate) qs.set('asOf', asOfDate);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/quantity-analytics?${qs.toString()}`);
      if (response.data.success) {
        setQuantity3Data(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching quantity3 analytics:', error);
    } finally {
      setQuantity3Loading(false);
    }
  }, [projectId, instance, accounts, quantity3GroupBy, asOfDate]);

  const fetchQuantity4Analytics = useCallback(async () => {
    if (!projectId) return;
    setQuantity4Loading(true);
    try {
      const qs = new URLSearchParams({ groupBy: quantity4GroupBy });
      if (asOfDate) qs.set('asOf', asOfDate);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/matrix/project/${projectId}/quantity2-analytics?${qs.toString()}`);
      if (response.data.success) {
        setQuantity4Data(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching quantity4 analytics:', error);
    } finally {
      setQuantity4Loading(false);
    }
  }, [projectId, instance, accounts, quantity4GroupBy, asOfDate]);

  useEffect(() => {
    if (selectedView === 'quantity' && isPiping) {
      fetchQuantityAnalytics();
    }
    if (selectedView === 'material' && isPiping) {
      fetchMaterialAnalytics();
    }
    if (selectedView === 'quantity2' && isPiping) {
      fetchQuantity2Analytics();
    }
    if (selectedView === 'quantity3' && isPiping) {
      fetchQuantity3Analytics();
    }
    if (selectedView === 'quantity4' && isPiping) {
      fetchQuantity4Analytics();
    }
    if (selectedView === 'shopSummary' && isPiping) {
      fetchShopSummaryAnalytics();
    }
  }, [selectedView, isPiping, fetchQuantityAnalytics, fetchMaterialAnalytics, fetchQuantity2Analytics, fetchQuantity3Analytics, fetchQuantity4Analytics, fetchShopSummaryAnalytics]);

  const handleSendHoldReport = useCallback(async () => {
    if (!projectId || sendingReport) return;
    setSendingReport(true);
    setSendResult(null);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/matrix/project/${projectId}/send-hold-report`);
      if (response.data.success) {
        setSendResult({ type: 'success', message: response.data.message || `Report sent to ${response.data.data?.emailsSent || 0} recipient(s)` });
      } else {
        setSendResult({ type: 'error', message: response.data.message || 'Failed to send report' });
      }
    } catch (error) {
      setSendResult({ type: 'error', message: error.response?.data?.message || 'Failed to send hold report' });
    } finally {
      setSendingReport(false);
    }
  }, [projectId, sendingReport, instance, accounts]);

  const { viewers, isConnected, connectionState } = usePresence('progressAnalytics', disciplineId || 'global', true);
  
  const {
    settings, updateSetting, toggleMetric, toggleChart, setChartType, resetToDefaults,
    getChartType, filterByDateRange,
  } = useAnalyticsSettings(`progress_${disciplineId}`, AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const colors = { primary: '#6366f1', secondary: '#8b5cf6', success: '#10b981', warning: '#f59e0b', danger: '#ef4444', info: '#3b82f6', dark: '#1e293b', chartColors: ['#ef4444', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981'] };

  const formatNumber = (num, decimals = 1) => num.toFixed(decimals);

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', lineHeight: 1.35 }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title, formatValue, colorByValue }) => {
    const chartData = data.slice(0, 12);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const barColor = colorByValue ? (value >= 100 ? colors.success : value >= 50 ? colors.warning : colors.danger) : colors.chartColors[idx % colors.chartColors.length];
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '100px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item[labelKey]}</div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: '4px', transition: 'width 0.5s ease' }} />
                </div>
                <div style={{ width: '60px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {formatValue ? formatValue(value) : value.toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const ProgressRing = ({ value, size = 120, label = 'Average' }) => {
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const clamped = Math.max(0, Math.min(100, value || 0));
    const offset = circumference - (clamped / 100) * circumference;
    const color = value >= 100 ? colors.success : value >= 50 ? colors.warning : colors.danger;

    return (
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ display: 'block', transform: 'rotate(-90deg)' }}>
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark }}>{(value || 0).toFixed(1)}%</div>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{label}</div>
        </div>
      </div>
    );
  };

  // Top-level tabs. Some tabs group several views together and expose them via
  // a secondary sub-tab bar (mirrors the Shop Analytics sub-tab pattern).
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    ...(isPiping ? [
      { id: 'quantity', label: 'Quantity', icon: '📏' },
      { id: 'material', label: 'Material', icon: '🧱' },
      { id: 'shop', label: 'Shop', icon: '🏭' },
    ] : []),
    { id: 'service', label: 'Service', icon: '🔧' },
    { id: 'sov', label: 'SOV', icon: '📑' },
    { id: 'onHold', label: `Hold (${analytics.onHoldCount})`, icon: '⏸️' },
  ];

  // Sub-tabs for grouped top-level tabs. Each entry maps a leaf "view" to a label.
  // Shop additionally surfaces the Shop Analytics sub-tabs (shop1-3 + load) which
  // are driven via the shopAnalyticsTab state while the view stays on 'shopTabs'.
  const subTabConfig = {
    quantity: [
      { view: 'quantity', label: 'Quantity 1' },
      { view: 'quantity2', label: 'Quantity 2' },
      { view: 'quantity3', label: 'Quantity 3' },
      { view: 'quantity4', label: 'Quantity 4' },
    ],
    shop: [
      { view: 'shopSummary', label: 'Summary' },
      { view: 'shopTabs', shopTab: 'shop1', label: 'Shop 1' },
      { view: 'shopTabs', shopTab: 'shop2', label: 'Shop 2' },
      { view: 'shopTabs', shopTab: 'shop3', label: 'Shop 3' },
      { view: 'shopTabs', shopTab: 'load', label: 'Load' },
    ],
    sov: isPiping ? [
      { view: 'sov', label: 'SOV' },
      { view: 'sovAnalytics', label: 'SOV Analytics' },
      { view: 'sovWhatIf', label: 'SOV What-If' },
    ] : [],
  };

  // Maps a leaf view back to the top-level tab that owns it (for highlighting).
  const viewToTab = {
    overview: 'overview',
    quantity: 'quantity', quantity2: 'quantity', quantity3: 'quantity', quantity4: 'quantity',
    material: 'material',
    shopSummary: 'shop', shopTabs: 'shop',
    service: 'service',
    sov: 'sov', sovAnalytics: 'sov', sovWhatIf: 'sov',
    onHold: 'onHold',
  };

  // The default view shown when a top-level tab is first selected.
  const tabDefaultView = {
    overview: 'overview',
    quantity: 'quantity', material: 'material', shop: 'shopSummary',
    service: 'service', sov: 'sov', onHold: 'onHold',
  };

  // Stable id for a sub-tab, used in the show/hide picker ('sub:' prefix keeps
  // it from colliding with top-level tab ids).
  const subTabId = (st) => `sub:${st.shopTab ? `${st.view}:${st.shopTab}` : st.view}`;

  const activeTopTab = viewToTab[selectedView] || 'overview';
  const activeSubTabs = (subTabConfig[activeTopTab] || []).filter(st => !hiddenTabs.has(subTabId(st)));

  const isSubTabActive = (st) => (
    st.shopTab
      ? selectedView === st.view && shopAnalyticsTab === st.shopTab
      : selectedView === st.view
  );

  const handleSelectTopTab = (tabId) => {
    const subs = subTabConfig[tabId] || [];
    const visibleSubs = subs.filter(st => !hiddenTabs.has(subTabId(st)));
    // Land on the first visible sub-tab; if the user hid them all, fall back
    // to the first sub-tab so the top tab still shows something.
    const target = visibleSubs[0] || subs[0];
    if (target) {
      setSelectedView(target.view);
      if (target.shopTab) setShopAnalyticsTab(target.shopTab);
    } else {
      setSelectedView(tabDefaultView[tabId] || tabId);
    }
  };

  const handleSelectSubTab = (st) => {
    setSelectedView(st.view);
    if (st.shopTab) setShopAnalyticsTab(st.shopTab);
  };

  const renderOverview = () => {
    const a = analytics;
    const fmtHrs = (v) => (v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
    const pct = (n, d) => (d > 0 ? (n / d) * 100 : 0);
    const completedPct = pct(a.completed, a.totalRows);

    const AttentionCard = ({ icon, color, label, value, detail }) => (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '1.25rem' }}>{icon}</span>
          <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>{label}</span>
        </div>
        <div style={{ fontSize: '1.6rem', fontWeight: '700', color: colors.dark }}>{value}</div>
        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>{detail}</div>
      </div>
    );

    return (
      <>
        {/* Hero KPIs — headline health of the project */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard title="Total Items" value={a.totalRows} icon="📋" color={colors.primary} />
          {a.hasCreditSplit ? (
            <>
              <MetricCard title="Fab Complete" value={a.shopComplete} subtitle={`${pct(a.shopComplete, a.totalRows).toFixed(1)}% of items`} icon="🏭" color={colors.success} />
              <MetricCard title="Install Complete" value={a.fieldComplete} subtitle={`${pct(a.fieldComplete, a.totalRows).toFixed(1)}% of items`} icon="✅" color={colors.success} />
            </>
          ) : (
            <MetricCard title="Completed" value={a.completed} subtitle={`${completedPct.toFixed(1)}% of items`} icon="✅" color={colors.success} />
          )}
          {a.hasHoursData && <MetricCard title="Earned Hours" value={`${a.hrsProgress.toFixed(1)}%`} subtitle={`${fmtHrs(a.totalEarnedHrs)} / ${fmtHrs(a.totalBudgetHrs)} hrs`} icon="⏱️" color={colors.info} />}
          {a.hasHoursData && <MetricCard title="Remaining Hours" value={fmtHrs(a.remainingHrs)} subtitle={`of ${fmtHrs(a.totalBudgetHrs)} budgeted`} icon="🛠️" color={colors.warning} />}
          {a.totalCredit > 0 && (a.hasCreditSplit ? (
            <>
              <MetricCard title="Shop (Fab) Credit" value={`${a.shopCreditProgress.toFixed(1)}%`} subtitle={`${fmtHrs(a.totalEarnedCreditShop)} / ${fmtHrs(a.fabEligibleCredit || a.totalCredit)}`} icon="🏭" color="#f59e0b" />
              <MetricCard title="Field (Install) Credit" value={`${a.fieldCreditProgress.toFixed(1)}%`} subtitle={`${fmtHrs(a.totalEarnedCreditField)} / ${fmtHrs(a.totalCredit)}`} icon="🔧" color="#10b981" />
            </>
          ) : (
            <MetricCard title="Earned Credit" value={`${a.creditProgress.toFixed(1)}%`} subtitle={`${fmtHrs(a.totalEarnedCredit)} / ${fmtHrs(a.totalCredit)}`} icon="💵" color="#7c3aed" />
          ))}
          {a.onHoldCount > 0 && <MetricCard title="On Hold" value={a.onHoldCount} subtitle={`${pct(a.onHoldCount, a.totalRows).toFixed(1)}% of items`} icon="⏸️" color="#dc2626" />}
        </div>

        {/* Progress ring + where the remaining work sits */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{a.hasCreditSplit ? 'Credit Progress' : 'Overall Progress'}</h4>
            {a.hasCreditSplit ? (
              <div style={{ display: 'flex', gap: '1.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <ProgressRing value={a.shopCreditProgress} size={110} label="Shop (Fab)" />
                <ProgressRing value={a.fieldCreditProgress} size={110} label="Field (Install)" />
              </div>
            ) : (
              <ProgressRing value={a.avgProgress} size={150} />
            )}
          </div>
          {a.hasHoursData && a.byAreaRemaining.length > 0 ? (
            <BarChart data={a.byAreaRemaining} valueKey="remainingHrs" labelKey="name" title="Remaining Hours by Area (where the work is)" formatValue={(v) => `${fmtHrs(v)} h`} />
          ) : (
            <BarChart data={a.byArea} valueKey="count" labelKey="name" title="Items by Area" />
          )}
        </div>

        {/* Piping: separate fab vs install progress distributions (never combined) */}
        {a.hasCreditSplit && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <BarChart data={a.shopDistribution} valueKey="count" labelKey="name" title="🏭 Shop (Fab) Progress Distribution" formatValue={(v) => `${v}`} />
            <BarChart data={a.fieldDistribution} valueKey="count" labelKey="name" title="🔧 Field (Install) Progress Distribution" formatValue={(v) => `${v}`} />
          </div>
        )}

        {/* Needs attention — the act-now list */}
        <h4 style={{ margin: '0 0 0.75rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Needs Attention</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {a.hasCreditSplit ? (
            <>
              <AttentionCard
                icon="📦" color={colors.warning} label="Fabricated, Not Installed"
                value={a.fabbedNotInstalled}
                detail="Fab complete · awaiting field install"
              />
              <AttentionCard
                icon="🏭" color={colors.danger} label="Fab Not Started"
                value={a.shopNotStarted}
                detail={`${pct(a.shopNotStarted, a.totalRows).toFixed(1)}% of items at 0% fab`}
              />
              <AttentionCard
                icon="🔧" color={colors.danger} label="Install Not Started"
                value={a.fieldNotStarted}
                detail={`${pct(a.fieldNotStarted, a.totalRows).toFixed(1)}% of items at 0% install`}
              />
            </>
          ) : (
            <>
              <AttentionCard
                icon="⏳" color={colors.danger} label="Not Started"
                value={a.notStarted}
                detail={`${pct(a.notStarted, a.totalRows).toFixed(1)}% of items at 0%`}
              />
              <AttentionCard
                icon="🏁" color={colors.success} label="Almost Done (76–99%)"
                value={a.almostDoneCount}
                detail={a.hasHoursData ? `~${fmtHrs(a.almostDoneRemainingHrs)} hrs to close out` : 'Push these over the line'}
              />
            </>
          )}
          <AttentionCard
            icon="⏸️" color="#dc2626" label="On Hold"
            value={a.onHoldCount}
            detail={a.onHoldCount > 0
              ? `${a.hasHoursData ? `${fmtHrs(a.onHoldRemainingHrs)} hrs tied up · ` : ''}oldest ${a.oldestHoldDays}d`
              : 'Nothing on hold'}
          />
        </div>
      </>
    );
  };

  const formatHoldDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return '—'; }
  };

  const renderOnHold = () => {
    const items = analytics.onHoldItems;
    if (items.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No Items On Hold</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>All items are currently active.</p>
        </div>
      );
    }

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>
            ⏸️ Items Currently On Hold
            <span style={{ marginLeft: '0.5rem', padding: '2px 8px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600' }}>
              {items.length}
            </span>
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {sendResult && (
              <span style={{
                fontSize: '0.8rem',
                color: sendResult.type === 'success' ? '#059669' : '#dc2626',
                padding: '4px 10px',
                backgroundColor: sendResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                borderRadius: '6px',
              }}>
                {sendResult.message}
              </span>
            )}
            <button
              onClick={handleSendHoldReport}
              disabled={sendingReport}
              style={{
                padding: '0.5rem 1rem', backgroundColor: sendingReport ? '#9ca3af' : '#dc2626',
                color: 'white', border: 'none', borderRadius: '8px',
                cursor: sendingReport ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: '500',
                display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'background-color 0.2s',
              }}
              title="Send the hold list to everyone assigned to this project"
            >
              {sendingReport ? '⏳ Sending...' : '📧 Send Report'}
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>ISO Sheet</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Fluid Code</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>P&ID</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Size</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Material</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Comments</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Put on Hold By</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Hold Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: '500', color: '#1e293b' }}>{item.isoSheetNumber || item.iso_sheet_number || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>{item.fluidCode || item.fluid_code || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>{item.pAndId || item.pnid || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>{item.size1 || item.size_1 || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>{item.material1 || item.material_1 || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#475569', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.comments || ''}>{item.comments || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#6b7280', fontSize: '0.75rem' }}>{item.holdUpdatedBy || item.hold_updated_by || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#6b7280', fontSize: '0.75rem' }}>{formatHoldDate(item.holdUpdatedAt || item.hold_updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSOV = () => {
    if (!sovAnalytics.hasData) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📑</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No SOV Data Available</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>SOV codes have not been assigned to any items in this tracker.</p>
        </div>
      );
    }

    const { groups } = sovAnalytics;
    const totalItems = groups.reduce((s, g) => s + g.count, 0);
    const totBudgetHrs = groups.reduce((s, g) => s + g.totalBudgetHrs, 0);
    const totEarnedHrs = groups.reduce((s, g) => s + g.earnedHrs, 0);
    const totCredit = groups.reduce((s, g) => s + g.totalCredit, 0);
    const totEarnedCredit = groups.reduce((s, g) => s + g.earnedCredit, 0);
    const totEarnedShop = groups.reduce((s, g) => s + g.earnedCreditShop, 0);
    const totEarnedField = groups.reduce((s, g) => s + g.earnedCreditField, 0);
    const totFabEligible = groups.reduce((s, g) => s + (g.fabEligibleCredit || 0), 0);
    const uniqueSovCount = groups.filter(g => g.sovCode !== 'Unassigned').length;
    const sovPct = (e, t) => { const tv = parseFloat(t) || 0; return tv > 0 ? Math.round((parseFloat(e) / tv) * 1000) / 10 : 0; };
    const fmtH = (v) => { const n = parseFloat(v) || 0; return n.toLocaleString('en-US', { maximumFractionDigits: 1 }); };
    const showHours = sovViewMode === 'internal';
    const barPct = (g) => (showHours ? g.hrsProgress : (isPiping ? g.fieldProgress : g.creditProgress));
    const totBarPct = showHours
      ? sovPct(totEarnedHrs, totBudgetHrs)
      : (isPiping ? sovPct(totEarnedField, totCredit) : sovPct(totEarnedCredit, totCredit));
    const barLabel = showHours ? 'Progress (Hrs)' : (isPiping ? 'Progress (Field)' : 'Progress (Credit)');

    const sovThStyle = { padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const sovTdStyle = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '13px' };
    const getProgressColor = (val) => val >= 100 ? colors.success : val >= 50 ? colors.warning : val > 0 ? '#f59e0b' : '#94a3b8';

    return (
      <>
        <SovAudienceToggle
          value={sovViewMode}
          onChange={setSovViewMode}
          hint={showHours
            ? 'Internal view shows hours and credits.'
            : (isPiping
              ? 'Client view is credits only. Shop (fab) and field (install) are separate and are not added together.'
              : 'Client view is credits only.')}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="SOV Codes" value={uniqueSovCount} icon="📑" color={colors.primary} />
          <MetricCard title="Total Items" value={totalItems} icon="📋" color={colors.info} />
          {showHours && (
            <>
              <MetricCard title="Total Hours" value={fmtH(totBudgetHrs)} icon="⏱️" color="#1e40af" />
              <MetricCard title="Earned Hours" value={fmtH(totEarnedHrs)} subtitle={`${sovPct(totEarnedHrs, totBudgetHrs)}% of budget`} icon="✅" color={colors.success} />
            </>
          )}
          <MetricCard title="Total Credit" value={fmtH(totCredit)} icon="💰" color="#7c3aed" />
          {isPiping ? (
            <>
              <MetricCard title="Shop (Fab) Credit" value={fmtH(totEarnedShop)} subtitle={`${sovPct(totEarnedShop, totFabEligible || totCredit)}% of shop credit`} icon="🏭" color="#f59e0b" />
              <MetricCard title="Field (Install) Credit" value={fmtH(totEarnedField)} subtitle={`${sovPct(totEarnedField, totCredit)}% of credit`} icon="🔧" color="#059669" />
            </>
          ) : (
            <MetricCard title="Earned Credit" value={fmtH(totEarnedCredit)} subtitle={`${sovPct(totEarnedCredit, totCredit)}% of total`} icon="💵" color="#059669" />
          )}
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ ...sovThStyle, textAlign: 'left' }}>SOV Code</th>
                  <th style={sovThStyle}>Items</th>
                  {showHours && (
                    <>
                      <th style={{ ...sovThStyle, backgroundColor: '#eff6ff' }}>Total Hrs</th>
                      <th style={{ ...sovThStyle, backgroundColor: '#eff6ff' }}>Earned Hrs</th>
                      <th style={{ ...sovThStyle, backgroundColor: '#eff6ff' }}>Hrs %</th>
                    </>
                  )}
                  <th style={{ ...sovThStyle, backgroundColor: '#fefce8' }}>Total Credit</th>
                  {isPiping ? (
                    <>
                      <th style={{ ...sovThStyle, backgroundColor: '#fff7ed' }}>Shop (Fab)</th>
                      <th style={{ ...sovThStyle, backgroundColor: '#fff7ed' }}>Shop %</th>
                      <th style={{ ...sovThStyle, backgroundColor: '#eef2ff' }}>Field (Install)</th>
                      <th style={{ ...sovThStyle, backgroundColor: '#eef2ff' }}>Field %</th>
                    </>
                  ) : (
                    <>
                      <th style={{ ...sovThStyle, backgroundColor: '#fefce8' }}>Earned Credit</th>
                      <th style={{ ...sovThStyle, backgroundColor: '#fefce8' }}>Credit %</th>
                    </>
                  )}
                  <th style={{ ...sovThStyle, width: '160px' }}>{barLabel}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g, idx) => (
                  <tr key={g.sovCode} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ ...sovTdStyle, textAlign: 'left', fontWeight: '600', color: g.sovCode === 'Unassigned' ? '#94a3b8' : '#1e293b' }}>{g.sovCode}</td>
                    <td style={sovTdStyle}>{g.count}</td>
                    {showHours && (
                      <>
                        <td style={{ ...sovTdStyle, backgroundColor: '#f8faff' }}>{fmtH(g.totalBudgetHrs)}</td>
                        <td style={{ ...sovTdStyle, backgroundColor: '#f8faff' }}>{fmtH(g.earnedHrs)}</td>
                        <td style={{ ...sovTdStyle, backgroundColor: '#f8faff', fontWeight: '600', color: getProgressColor(g.hrsProgress) }}>{g.hrsProgress}%</td>
                      </>
                    )}
                    <td style={{ ...sovTdStyle, backgroundColor: '#fffef5' }}>{fmtH(g.totalCredit)}</td>
                    {isPiping ? (
                      <>
                        <td style={{ ...sovTdStyle, backgroundColor: '#fffaf3' }}>{fmtH(g.earnedCreditShop)}</td>
                        <td style={{ ...sovTdStyle, backgroundColor: '#fffaf3', fontWeight: '600', color: getProgressColor(g.shopProgress) }}>{g.shopProgress}%</td>
                        <td style={{ ...sovTdStyle, backgroundColor: '#f6f7ff' }}>{fmtH(g.earnedCreditField)}</td>
                        <td style={{ ...sovTdStyle, backgroundColor: '#f6f7ff', fontWeight: '600', color: getProgressColor(g.fieldProgress) }}>{g.fieldProgress}%</td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...sovTdStyle, backgroundColor: '#fffef5' }}>{fmtH(g.earnedCredit)}</td>
                        <td style={{ ...sovTdStyle, backgroundColor: '#fffef5', fontWeight: '600', color: getProgressColor(g.creditProgress) }}>{g.creditProgress}%</td>
                      </>
                    )}
                    <td style={{ ...sovTdStyle, padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '16px', backgroundColor: '#e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(barPct(g), 100)}%`, height: '100%', backgroundColor: getProgressColor(barPct(g)), borderRadius: '8px', transition: 'width 0.3s ease' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b', minWidth: '32px', textAlign: 'right' }}>{barPct(g)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{totalItems}</td>
                  {showHours && (
                    <>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{fmtH(totBudgetHrs)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{fmtH(totEarnedHrs)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{sovPct(totEarnedHrs, totBudgetHrs)}%</td>
                    </>
                  )}
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtH(totCredit)}</td>
                  {isPiping ? (
                    <>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{fmtH(totEarnedShop)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{sovPct(totEarnedShop, totFabEligible || totCredit)}%</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{fmtH(totEarnedField)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{sovPct(totEarnedField, totCredit)}%</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{fmtH(totEarnedCredit)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{sovPct(totEarnedCredit, totCredit)}%</td>
                    </>
                  )}
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, height: '16px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(totBarPct, 100)}%`, height: '100%', backgroundColor: colors.success, borderRadius: '8px' }} />
                      </div>
                      <span style={{ fontSize: '11px', minWidth: '32px', textAlign: 'right' }}>{totBarPct}%</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </>
    );
  };

  const fmtFt = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const fmtCr = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  };

  const pctOf = (part, total) => {
    const t = parseFloat(total) || 0;
    return t > 0 ? ((parseFloat(part) / t) * 100).toFixed(1) : '0.0';
  };

  const renderService = () => {
    if (!serviceAnalytics.hasData) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔧</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No Credit Data Available</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Import credit values in your tracker to see the service summary.</p>
        </div>
      );
    }

    const { totals } = serviceAnalytics;
    const sections = serviceAnalytics.sections || [];
    const fabSub = formatFabSplitSubtitle({
      ...(serviceAnalytics.fabSplit || {}),
      overallFab: totals.fabCredit,
      overallTotal: totals.totalCredit,
    });
    // Field Run keeps its amber accent; every other section band is blue.
    const sectionMeta = (label) => label === 'Field Run'
      ? { accent: '#b45309', headerBg: '#fef3c7' }
      : { accent: '#1e3a5f', headerBg: '#e0f2fe' };
    const showSectionBands = serviceAnalytics.banded;
    const thS = { padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px' };
    const getProgressColor = (val) => val >= 100 ? colors.success : val >= 50 ? colors.warning : val > 0 ? '#f59e0b' : '#94a3b8';
    const fcColors = ['#1e40af', '#065f46', '#92400e', '#7c3aed', '#be185d', '#0f766e', '#b91c1c', '#4338ca'];
    const selectS = { padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#1e293b', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600' };

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="Total Items" value={totals.items} icon="📋" color={colors.primary} />
          <MetricCard title="Total Credit" value={fmtCr(totals.totalCredit)} icon="🔧" color="#7c3aed" />
          <MetricCard title="Fab Earned Credit" value={fmtCr(totals.fabCredit)} subtitle={fabSub} icon="🏭" color={colors.warning} />
          <MetricCard title="Install Earned Credit" value={fmtCr(totals.instCredit)} subtitle={`${pctOf(totals.instCredit, totals.totalCredit)}% of total`} icon="✅" color={colors.success} />
          <MetricCard title="Remaining Credit" value={fmtCr(totals.totalCredit - totals.instCredit)} subtitle={`${pctOf(totals.totalCredit - totals.instCredit, totals.totalCredit)}% of total`} icon="📉" color={colors.danger} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Group by:</span>
          <select value={serviceGroupBy1} onChange={(e) => setServiceGroupBy1(e.target.value)} style={selectS}>
            {SERVICE_GROUP_OPTIONS.map(o => <option key={o.id} value={o.id} disabled={o.id === serviceGroupBy2}>{o.label}</option>)}
          </select>
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>then by:</span>
          <select value={serviceGroupBy2} onChange={(e) => setServiceGroupBy2(e.target.value)} style={selectS}>
            {SERVICE_GROUP_OPTIONS.map(o => <option key={o.id} value={o.id} disabled={o.id === serviceGroupBy1}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ ...thS, textAlign: 'left' }}>{serviceGroupLabel(serviceGroupBy2)}</th>
                  <th style={thS}>Items</th>
                  <th style={{ ...thS, backgroundColor: '#f5f3ff' }}>Total Credit</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fab Earned</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fab %</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Install Earned</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Install %</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining</th>
                  <th style={{ ...thS, width: '140px' }}>Fab Progress</th>
                  <th style={{ ...thS, width: '140px' }}>Install Progress</th>
                </tr>
              </thead>
              <tbody>
                {sections.map(section => {
                  const meta = sectionMeta(section.label);
                  const st = section.totals;
                  const secFabPct = st.fabEligibleCredit > 0 ? parseFloat(pctOf(st.fabCredit, st.fabEligibleCredit)) : null;
                  const secInstPct = parseFloat(pctOf(st.instCredit, st.totalCredit));
                  return (
                    <React.Fragment key={section.key}>
                      {showSectionBands && (
                        <tr style={{ backgroundColor: meta.headerBg }}>
                          <td colSpan={10} style={{ padding: '10px 12px', fontWeight: '800', fontSize: '13px', color: meta.accent, borderLeft: `5px solid ${meta.accent}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {section.label}
                          </td>
                        </tr>
                      )}
                      {section.groups.map((g, idx) => {
                        const remaining = g.totalCredit - g.instCredit;
                        const fabPct = g.fabEligibleCredit > 0 ? parseFloat(pctOf(g.fabCredit, g.fabEligibleCredit)) : null;
                        const instPct = parseFloat(pctOf(g.instCredit, g.totalCredit));
                        const fc = fcColors[idx % fcColors.length];
                        return (
                          <tr key={g.label} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', fontWeight: '600', color: fc, borderLeft: `3px solid ${fc}` }}>{g.label}</td>
                            <td style={tdS}>{g.items}</td>
                            <td style={{ ...tdS, backgroundColor: '#faf8ff', fontWeight: '600' }}>{fmtCr(g.totalCredit)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtCr(g.fabCredit)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5', fontWeight: '600', color: fabPct == null ? '#94a3b8' : getProgressColor(fabPct) }}>{fabPct == null ? 'N/A' : `${fabPct}%`}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtCr(g.instCredit)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8', fontWeight: '600', color: getProgressColor(instPct) }}>{instPct}%</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtCr(remaining)}</td>
                            <td style={{ ...tdS, padding: '7px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ flex: 1, height: '14px', backgroundColor: '#e2e8f0', borderRadius: '7px', overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(fabPct || 0, 100)}%`, height: '100%', backgroundColor: fabPct == null ? '#cbd5e1' : getProgressColor(fabPct), borderRadius: '7px', transition: 'width 0.3s ease' }} />
                                </div>
                                <span style={{ fontSize: '10px', color: '#64748b', minWidth: '30px', textAlign: 'right' }}>{fabPct == null ? 'N/A' : `${fabPct}%`}</span>
                              </div>
                            </td>
                            <td style={{ ...tdS, padding: '7px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ flex: 1, height: '14px', backgroundColor: '#e2e8f0', borderRadius: '7px', overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(instPct, 100)}%`, height: '100%', backgroundColor: getProgressColor(instPct), borderRadius: '7px', transition: 'width 0.3s ease' }} />
                                </div>
                                <span style={{ fontSize: '10px', color: '#64748b', minWidth: '30px', textAlign: 'right' }}>{instPct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {showSectionBands && (
                        <tr style={{ backgroundColor: meta.accent, color: 'white', fontWeight: '700', fontSize: '12.5px' }}>
                          <td style={{ padding: '9px 12px', textAlign: 'left' }}>{section.label} - Subtotal</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{st.items}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtCr(st.totalCredit)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtCr(st.fabCredit)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{secFabPct == null ? 'N/A' : `${secFabPct}%`}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtCr(st.instCredit)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{secInstPct}%</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtCr(st.totalCredit - st.instCredit)}</td>
                          <td style={{ padding: '9px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ flex: 1, height: '14px', backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '7px', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(secFabPct || 0, 100)}%`, height: '100%', backgroundColor: colors.warning, borderRadius: '7px' }} />
                              </div>
                              <span style={{ fontSize: '10px', minWidth: '30px', textAlign: 'right' }}>{secFabPct == null ? 'N/A' : `${secFabPct}%`}</span>
                            </div>
                          </td>
                          <td style={{ padding: '9px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ flex: 1, height: '14px', backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '7px', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(secInstPct, 100)}%`, height: '100%', backgroundColor: colors.success, borderRadius: '7px' }} />
                              </div>
                              <span style={{ fontSize: '10px', minWidth: '30px', textAlign: 'right' }}>{secInstPct}%</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{totals.items}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtCr(totals.totalCredit)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtCr(totals.fabCredit)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{pctOf(totals.fabCredit, totals.fabEligibleCredit || totals.totalCredit)}%</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtCr(totals.instCredit)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{pctOf(totals.instCredit, totals.totalCredit)}%</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtCr(totals.totalCredit - totals.instCredit)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1, height: '14px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '7px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(parseFloat(pctOf(totals.fabCredit, totals.fabEligibleCredit || totals.totalCredit)), 100)}%`, height: '100%', backgroundColor: colors.warning, borderRadius: '7px' }} />
                      </div>
                      <span style={{ fontSize: '10px', minWidth: '30px', textAlign: 'right' }}>{pctOf(totals.fabCredit, totals.fabEligibleCredit || totals.totalCredit)}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1, height: '14px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '7px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(parseFloat(pctOf(totals.instCredit, totals.totalCredit)), 100)}%`, height: '100%', backgroundColor: colors.success, borderRadius: '7px' }} />
                      </div>
                      <span style={{ fontSize: '10px', minWidth: '30px', textAlign: 'right' }}>{pctOf(totals.instCredit, totals.totalCredit)}%</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderQuantity = () => {
    if (quantityLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading quantity data...</div>
          <div style={{ fontSize: '0.85rem' }}>Aggregating footage by area and fluid code</div>
        </div>
      );
    }

    if (!quantityData || !quantityData.rows || quantityData.rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📏</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No Footage Data Available</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Import footage columns (SB Footage, LB Footage) in your matrix upload to see quantity analytics.</p>
        </div>
      );
    }

    const { rows, summary } = quantityData;

    // Split the table into Field Run vs shop-fabricated sections, each with
    // its own subtotal row (fabrication_shop = 'Field Run' drives the flag).
    const fieldRunRows = rows.filter(r => r.is_field_run);
    const restRows = rows.filter(r => !r.is_field_run);
    const sections = [];
    if (fieldRunRows.length > 0) sections.push({ key: 'fieldRun', label: 'Field Run', rows: fieldRunRows, accent: '#b45309', headerBg: '#fef3c7' });
    if (restRows.length > 0) sections.push({ key: 'rest', label: 'Shop Fabricated', rows: restRows, accent: '#1e3a5f', headerBg: '#e0f2fe' });
    const showSectionBands = sections.length > 1;

    const thS = { padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px' };

    const computeSectionTotals = (sectionRows) => {
      return sectionRows.reduce((acc, r) => ({
        iso_count: acc.iso_count + (parseInt(r.iso_count) || 0),
        total_sb_ft: acc.total_sb_ft + (parseFloat(r.total_sb_ft) || 0),
        total_lb_ft: acc.total_lb_ft + (parseFloat(r.total_lb_ft) || 0),
        fab_sb_ft: acc.fab_sb_ft + (parseFloat(r.fab_sb_ft) || 0),
        fab_lb_ft: acc.fab_lb_ft + (parseFloat(r.fab_lb_ft) || 0),
        installed_sb_ft: acc.installed_sb_ft + (parseFloat(r.installed_sb_ft) || 0),
        installed_lb_ft: acc.installed_lb_ft + (parseFloat(r.installed_lb_ft) || 0),
      }), { iso_count: 0, total_sb_ft: 0, total_lb_ft: 0, fab_sb_ft: 0, fab_lb_ft: 0, installed_sb_ft: 0, installed_lb_ft: 0 });
    };

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb;
    const fabAll = gFsb + gFlb;
    const instAll = gIsb + gIlb;
    const fabSub = formatFabSplitSubtitle({ ...fabSplitFromQuantityRows(rows), overallFab: fabAll, overallTotal: totalAll });

    return (
      <div ref={contentRef}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="Total ISOs" value={parseInt(gs.total_isos) || 0} icon="📋" color={colors.primary} />
          <MetricCard title="Total Footage" value={fmtFt(totalAll) + ' ft'} subtitle={`SB: ${fmtFt(gTsb)} | LB: ${fmtFt(gTlb)}`} icon="📏" color="#1e40af" />
          <MetricCard title="Fabricated" value={fmtFt(fabAll) + ' ft'} subtitle={fabSub} icon="🏭" color={colors.warning} />
          <MetricCard title="Installed" value={fmtFt(instAll) + ' ft'} subtitle={totalAll > 0 ? `${((instAll / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="✅" color={colors.success} />
          <MetricCard title="Remaining" value={fmtFt(totalAll - instAll) + ' ft'} subtitle={totalAll > 0 ? `${(((totalAll - instAll) / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="🔧" color={colors.danger} />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={thS}>Fluid Code</th>
                  <th style={thS}>ISO Count</th>
                  <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Total FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#ecfdf5' }}>Total FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (LB)</th>
                </tr>
              </thead>
              <tbody>
                {sections.map(section => {
                  const sectionTotals = computeSectionTotals(section.rows);
                  return (
                    <React.Fragment key={section.key}>
                      {showSectionBands && (
                        <tr style={{ backgroundColor: section.headerBg }}>
                          <td colSpan={10} style={{ padding: '10px 12px', fontWeight: '800', fontSize: '13px', color: section.accent, borderLeft: `5px solid ${section.accent}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {section.label}
                          </td>
                        </tr>
                      )}
                      {section.rows.map((row, rowIdx) => {
                        const sbRem = (parseFloat(row.total_sb_ft) || 0) - (parseFloat(row.installed_sb_ft) || 0);
                        const lbRem = (parseFloat(row.total_lb_ft) || 0) - (parseFloat(row.installed_lb_ft) || 0);
                        return (
                          <tr key={`${section.key}-${rowIdx}`} style={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ ...tdS, fontWeight: '600', textAlign: 'left', paddingLeft: '20px' }}>{row.fluid_code}</td>
                            <td style={tdS}>{parseInt(row.iso_count) || 0}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8faff' }}>{fmtFt(row.total_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.total_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(sbRem)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(lbRem)}</td>
                          </tr>
                        );
                      })}
                      {showSectionBands && (
                        <tr style={{ backgroundColor: section.accent, color: 'white', fontWeight: '700', fontSize: '12.5px' }}>
                          <td style={{ padding: '9px 12px', textAlign: 'left' }}>{section.label} - Subtotal</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{sectionTotals.iso_count}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_lb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.fab_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.fab_lb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.installed_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.installed_lb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_sb_ft - sectionTotals.installed_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_lb_ft - sectionTotals.installed_lb_ft)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.total_isos) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb - gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb - gIlb)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderQuantity2 = () => {
    if (quantity2Loading) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading quantity data...</div>
          <div style={{ fontSize: '0.85rem' }}>Aggregating footage by area and fluid code (transportation-based install)</div>
        </div>
      );
    }

    if (!quantity2Data || !quantity2Data.rows || quantity2Data.rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📐</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No Footage Data Available</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Import footage columns (SB Footage, LB Footage) in your matrix upload to see quantity analytics.</p>
        </div>
      );
    }

    const { rows, summary } = quantity2Data;

    // Same Field Run vs shop-fabricated split as Quantity 1.
    const fieldRunRows = rows.filter(r => r.is_field_run);
    const restRows = rows.filter(r => !r.is_field_run);
    const sections = [];
    if (fieldRunRows.length > 0) sections.push({ key: 'fieldRun', label: 'Field Run', rows: fieldRunRows, accent: '#b45309', headerBg: '#fef3c7' });
    if (restRows.length > 0) sections.push({ key: 'rest', label: 'Shop Fabricated', rows: restRows, accent: '#1e3a5f', headerBg: '#e0f2fe' });
    const showSectionBands = sections.length > 1;

    const thS = { padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px' };

    const computeSectionTotals = (sectionRows) => {
      return sectionRows.reduce((acc, r) => ({
        iso_count: acc.iso_count + (parseInt(r.iso_count) || 0),
        total_sb_ft: acc.total_sb_ft + (parseFloat(r.total_sb_ft) || 0),
        total_lb_ft: acc.total_lb_ft + (parseFloat(r.total_lb_ft) || 0),
        fab_sb_ft: acc.fab_sb_ft + (parseFloat(r.fab_sb_ft) || 0),
        fab_lb_ft: acc.fab_lb_ft + (parseFloat(r.fab_lb_ft) || 0),
        installed_sb_ft: acc.installed_sb_ft + (parseFloat(r.installed_sb_ft) || 0),
        installed_lb_ft: acc.installed_lb_ft + (parseFloat(r.installed_lb_ft) || 0),
      }), { iso_count: 0, total_sb_ft: 0, total_lb_ft: 0, fab_sb_ft: 0, fab_lb_ft: 0, installed_sb_ft: 0, installed_lb_ft: 0 });
    };

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb;
    const fabAll = gFsb + gFlb;
    const instAll = gIsb + gIlb;
    const fabSub = formatFabSplitSubtitle({ ...fabSplitFromQuantityRows(rows), overallFab: fabAll, overallTotal: totalAll });

    return (
      <div>
        <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '0.8rem', color: '#1e40af', borderLeft: '3px solid #3b82f6' }}>
          Installed footage is based on <strong>Transportation to Installation Area</strong> step only (not full field completion).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="Total ISOs" value={parseInt(gs.total_isos) || 0} icon="📋" color={colors.primary} />
          <MetricCard title="Total Footage" value={fmtFt(totalAll) + ' ft'} subtitle={`SB: ${fmtFt(gTsb)} | LB: ${fmtFt(gTlb)}`} icon="📏" color="#1e40af" />
          <MetricCard title="Fabricated" value={fmtFt(fabAll) + ' ft'} subtitle={fabSub} icon="🏭" color={colors.warning} />
          <MetricCard title="Installed (Transport)" value={fmtFt(instAll) + ' ft'} subtitle={totalAll > 0 ? `${((instAll / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="🚚" color={colors.success} />
          <MetricCard title="Remaining" value={fmtFt(totalAll - instAll) + ' ft'} subtitle={totalAll > 0 ? `${(((totalAll - instAll) / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="🔧" color={colors.danger} />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={thS}>Fluid Code</th>
                  <th style={thS}>ISO Count</th>
                  <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Total FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#ecfdf5' }}>Total FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (LB)</th>
                </tr>
              </thead>
              <tbody>
                {sections.map(section => {
                  const sectionTotals = computeSectionTotals(section.rows);
                  return (
                    <React.Fragment key={section.key}>
                      {showSectionBands && (
                        <tr style={{ backgroundColor: section.headerBg }}>
                          <td colSpan={10} style={{ padding: '10px 12px', fontWeight: '800', fontSize: '13px', color: section.accent, borderLeft: `5px solid ${section.accent}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {section.label}
                          </td>
                        </tr>
                      )}
                      {section.rows.map((row, rowIdx) => {
                        const sbRem = (parseFloat(row.total_sb_ft) || 0) - (parseFloat(row.installed_sb_ft) || 0);
                        const lbRem = (parseFloat(row.total_lb_ft) || 0) - (parseFloat(row.installed_lb_ft) || 0);
                        return (
                          <tr key={`${section.key}-${rowIdx}`} style={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ ...tdS, fontWeight: '600', textAlign: 'left', paddingLeft: '20px' }}>{row.fluid_code}</td>
                            <td style={tdS}>{parseInt(row.iso_count) || 0}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8faff' }}>{fmtFt(row.total_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.total_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(sbRem)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(lbRem)}</td>
                          </tr>
                        );
                      })}
                      {showSectionBands && (
                        <tr style={{ backgroundColor: section.accent, color: 'white', fontWeight: '700', fontSize: '12.5px' }}>
                          <td style={{ padding: '9px 12px', textAlign: 'left' }}>{section.label} - Subtotal</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{sectionTotals.iso_count}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_lb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.fab_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.fab_lb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.installed_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.installed_lb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_sb_ft - sectionTotals.installed_sb_ft)}</td>
                          <td style={{ padding: '9px', textAlign: 'center' }}>{fmtFt(sectionTotals.total_lb_ft - sectionTotals.installed_lb_ft)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.total_isos) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb - gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb - gIlb)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Shared renderer for Quantity 3/4 - same table as Quantity 1/2 but grouped
  // by a user-selectable dimension (area, shop, system, ...) with group totals.
  const renderGroupedQuantity = ({ data, loading, groupBy, setGroupBy, banner, installedLabel, installedIcon }) => {
    const groupLabel = (QUANTITY_GROUP_OPTIONS.find(o => o.id === groupBy) || {}).label || 'Group';

    const selector = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Group by:</span>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#1e293b', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600' }}
        >
          {QUANTITY_GROUP_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </div>
    );

    if (loading) {
      return (
        <div>
          {selector}
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading quantity data...</div>
            <div style={{ fontSize: '0.85rem' }}>Aggregating footage by {groupLabel.toLowerCase()} and fluid code</div>
          </div>
        </div>
      );
    }

    if (!data || !data.rows || data.rows.length === 0) {
      return (
        <div>
          {selector}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📏</div>
            <h3 style={{ margin: 0, color: colors.dark }}>No Footage Data Available</h3>
            <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Import footage columns (SB Footage, LB Footage) in your matrix upload to see quantity analytics.</p>
          </div>
        </div>
      );
    }

    const { rows, summary } = data;

    const groupsMap = {};
    rows.forEach(row => {
      const key = row.group_key || 'Unspecified';
      if (!groupsMap[key]) groupsMap[key] = [];
      groupsMap[key].push(row);
    });
    const groupNames = Object.keys(groupsMap);
    const groupColors = ['#1e40af', '#065f46', '#92400e', '#7c3aed', '#be185d', '#0f766e', '#b91c1c', '#4338ca'];

    const thS = { padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px' };
    const tdL = { ...tdS, textAlign: 'left' };

    const computeGroupTotals = (groupRows) => {
      return groupRows.reduce((acc, r) => ({
        iso_count: acc.iso_count + (parseInt(r.iso_count) || 0),
        total_sb_ft: acc.total_sb_ft + (parseFloat(r.total_sb_ft) || 0),
        total_lb_ft: acc.total_lb_ft + (parseFloat(r.total_lb_ft) || 0),
        fab_sb_ft: acc.fab_sb_ft + (parseFloat(r.fab_sb_ft) || 0),
        fab_lb_ft: acc.fab_lb_ft + (parseFloat(r.fab_lb_ft) || 0),
        installed_sb_ft: acc.installed_sb_ft + (parseFloat(r.installed_sb_ft) || 0),
        installed_lb_ft: acc.installed_lb_ft + (parseFloat(r.installed_lb_ft) || 0),
      }), { iso_count: 0, total_sb_ft: 0, total_lb_ft: 0, fab_sb_ft: 0, fab_lb_ft: 0, installed_sb_ft: 0, installed_lb_ft: 0 });
    };

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb;
    const fabAll = gFsb + gFlb;
    const instAll = gIsb + gIlb;
    const fabSub = formatFabSplitSubtitle({ ...fabSplitFromQuantitySummary(gs), overallFab: fabAll, overallTotal: totalAll });

    return (
      <div>
        {banner && (
          <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '0.8rem', color: '#1e40af', borderLeft: '3px solid #3b82f6' }}>
            {banner}
          </div>
        )}
        {selector}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="Total ISOs" value={parseInt(gs.total_isos) || 0} icon="📋" color={colors.primary} />
          <MetricCard title="Total Footage" value={fmtFt(totalAll) + ' ft'} subtitle={`SB: ${fmtFt(gTsb)} | LB: ${fmtFt(gTlb)}`} icon="📏" color="#1e40af" />
          <MetricCard title="Fabricated" value={fmtFt(fabAll) + ' ft'} subtitle={fabSub} icon="🏭" color={colors.warning} />
          <MetricCard title={installedLabel || 'Installed'} value={fmtFt(instAll) + ' ft'} subtitle={totalAll > 0 ? `${((instAll / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon={installedIcon || '✅'} color={colors.success} />
          <MetricCard title="Remaining" value={fmtFt(totalAll - instAll) + ' ft'} subtitle={totalAll > 0 ? `${(((totalAll - instAll) / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="🔧" color={colors.danger} />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ ...thS, textAlign: 'left' }}>{groupLabel} / Fluid Code</th>
                  <th style={thS}>ISO Count</th>
                  <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Total FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#ecfdf5' }}>Total FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (LB)</th>
                </tr>
              </thead>
              <tbody>
                {groupNames.map((groupName, groupIdx) => {
                  const groupRows = groupsMap[groupName];
                  const groupTotals = computeGroupTotals(groupRows);
                  const groupColor = groupColors[groupIdx % groupColors.length];

                  return (
                    <React.Fragment key={groupName}>
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <td colSpan={10} style={{ padding: '8px 12px', fontWeight: '700', fontSize: '12px', color: groupColor, borderLeft: `4px solid ${groupColor}` }}>
                          {groupName}
                        </td>
                      </tr>
                      {groupRows.map((row, rowIdx) => {
                        const sbRem = (parseFloat(row.total_sb_ft) || 0) - (parseFloat(row.installed_sb_ft) || 0);
                        const lbRem = (parseFloat(row.total_lb_ft) || 0) - (parseFloat(row.installed_lb_ft) || 0);
                        return (
                          <tr key={`${groupName}-${rowIdx}`} style={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ ...tdS, fontWeight: '600', textAlign: 'left', paddingLeft: '20px' }}>{row.fluid_code}</td>
                            <td style={tdS}>{parseInt(row.iso_count) || 0}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8faff' }}>{fmtFt(row.total_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.total_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(sbRem)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(lbRem)}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ backgroundColor: '#e8ecf4', fontWeight: '700', borderBottom: '2px solid #cbd5e1' }}>
                        <td style={{ ...tdL, color: groupColor, paddingLeft: '12px' }}>{groupName} - Total</td>
                        <td style={tdS}>{groupTotals.iso_count}</td>
                        <td style={{ ...tdS, backgroundColor: '#e0e7ff' }}>{fmtFt(groupTotals.total_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#d1fae5' }}>{fmtFt(groupTotals.total_lb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fef9c3' }}>{fmtFt(groupTotals.fab_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fef9c3' }}>{fmtFt(groupTotals.fab_lb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#d1fae5' }}>{fmtFt(groupTotals.installed_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#d1fae5' }}>{fmtFt(groupTotals.installed_lb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fecaca' }}>{fmtFt(groupTotals.total_sb_ft - groupTotals.installed_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fecaca' }}>{fmtFt(groupTotals.total_lb_ft - groupTotals.installed_lb_ft)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.total_isos) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb - gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb - gIlb)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderQuantity3 = () => renderGroupedQuantity({
    data: quantity3Data,
    loading: quantity3Loading,
    groupBy: quantity3GroupBy,
    setGroupBy: setQuantity3GroupBy,
  });

  const renderQuantity4 = () => renderGroupedQuantity({
    data: quantity4Data,
    loading: quantity4Loading,
    groupBy: quantity4GroupBy,
    setGroupBy: setQuantity4GroupBy,
    banner: (<>Installed footage is based on <strong>Transportation to Installation Area</strong> step only (not full field completion).</>),
    installedLabel: 'Installed (Transport)',
    installedIcon: '🚚',
  });

  const renderMaterial = () => {
    if (materialLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading material data...</div>
          <div style={{ fontSize: '0.85rem' }}>Aggregating footage by shop and material</div>
        </div>
      );
    }

    if (!materialData || !materialData.rows || materialData.rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧱</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No Material Data Available</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Import material and footage columns in your matrix upload to see material analytics.</p>
        </div>
      );
    }

    const { rows, summary } = materialData;

    const shopGroups = {};
    rows.forEach(row => {
      const shop = row.shop;
      if (!shopGroups[shop]) shopGroups[shop] = [];
      shopGroups[shop].push(row);
    });

    const shopColors = ['#1e40af', '#065f46', '#92400e', '#7c3aed', '#be185d', '#0f766e', '#b91c1c', '#4338ca'];
    const shopNames = Object.keys(shopGroups);

    const thS = { padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px' };
    const tdL = { ...tdS, textAlign: 'left' };

    const computeShopTotals = (shopRows) => {
      return shopRows.reduce((acc, r) => ({
        iso_count: acc.iso_count + (parseInt(r.iso_count) || 0),
        total_sb_ft: acc.total_sb_ft + (parseFloat(r.total_sb_ft) || 0),
        total_lb_ft: acc.total_lb_ft + (parseFloat(r.total_lb_ft) || 0),
        fab_sb_ft: acc.fab_sb_ft + (parseFloat(r.fab_sb_ft) || 0),
        fab_lb_ft: acc.fab_lb_ft + (parseFloat(r.fab_lb_ft) || 0),
        installed_sb_ft: acc.installed_sb_ft + (parseFloat(r.installed_sb_ft) || 0),
        installed_lb_ft: acc.installed_lb_ft + (parseFloat(r.installed_lb_ft) || 0),
      }), { iso_count: 0, total_sb_ft: 0, total_lb_ft: 0, fab_sb_ft: 0, fab_lb_ft: 0, installed_sb_ft: 0, installed_lb_ft: 0 });
    };

    const gs = summary;
    const gTsb = parseFloat(gs.total_sb_ft) || 0, gTlb = parseFloat(gs.total_lb_ft) || 0;
    const gFsb = parseFloat(gs.fab_sb_ft) || 0, gFlb = parseFloat(gs.fab_lb_ft) || 0;
    const gIsb = parseFloat(gs.installed_sb_ft) || 0, gIlb = parseFloat(gs.installed_lb_ft) || 0;
    const totalAll = gTsb + gTlb;
    const fabAll = gFsb + gFlb;
    const instAll = gIsb + gIlb;

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="Total ISOs" value={parseInt(gs.total_isos) || 0} icon="📋" color={colors.primary} />
          <MetricCard title="Total Footage" value={fmtFt(totalAll) + ' ft'} subtitle={`SB: ${fmtFt(gTsb)} | LB: ${fmtFt(gTlb)}`} icon="📏" color="#1e40af" />
          <MetricCard title="Fabricated" value={fmtFt(fabAll) + ' ft'} subtitle={totalAll > 0 ? `${((fabAll / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="🏭" color={colors.warning} />
          <MetricCard title="Installed" value={fmtFt(instAll) + ' ft'} subtitle={totalAll > 0 ? `${((instAll / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="✅" color={colors.success} />
          <MetricCard title="Remaining" value={fmtFt(totalAll - instAll) + ' ft'} subtitle={totalAll > 0 ? `${(((totalAll - instAll) / totalAll) * 100).toFixed(1)}% of total` : '0%'} icon="🔧" color={colors.danger} />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={thS}>Material</th>
                  <th style={thS}>ISO Count</th>
                  <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Total FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#ecfdf5' }}>Total FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#f0fdf4' }}>Installed FT (LB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (SB)</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT (LB)</th>
                </tr>
              </thead>
              <tbody>
                {shopNames.map((shop, shopIdx) => {
                  const shopRows = shopGroups[shop];
                  const shopTotals = computeShopTotals(shopRows);
                  const shopColor = shopColors[shopIdx % shopColors.length];

                  return (
                    <React.Fragment key={shop}>
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <td colSpan={10} style={{ padding: '8px 12px', fontWeight: '700', fontSize: '12px', color: shopColor, borderLeft: `4px solid ${shopColor}` }}>
                          {shop}
                        </td>
                      </tr>
                      {shopRows.map((row, rowIdx) => {
                        const sbRem = (parseFloat(row.total_sb_ft) || 0) - (parseFloat(row.installed_sb_ft) || 0);
                        const lbRem = (parseFloat(row.total_lb_ft) || 0) - (parseFloat(row.installed_lb_ft) || 0);
                        return (
                          <tr key={`${shop}-${rowIdx}`} style={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ ...tdS, fontWeight: '600', textAlign: 'left', paddingLeft: '20px' }}>{row.material}</td>
                            <td style={tdS}>{parseInt(row.iso_count) || 0}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8faff' }}>{fmtFt(row.total_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.total_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(row.fab_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_sb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#f8fdf8' }}>{fmtFt(row.installed_lb_ft)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(sbRem)}</td>
                            <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(lbRem)}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ backgroundColor: '#e8ecf4', fontWeight: '700', borderBottom: '2px solid #cbd5e1' }}>
                        <td style={{ ...tdL, color: shopColor, paddingLeft: '12px' }}>{shop} - Total</td>
                        <td style={tdS}>{shopTotals.iso_count}</td>
                        <td style={{ ...tdS, backgroundColor: '#e0e7ff' }}>{fmtFt(shopTotals.total_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#d1fae5' }}>{fmtFt(shopTotals.total_lb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fef9c3' }}>{fmtFt(shopTotals.fab_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fef9c3' }}>{fmtFt(shopTotals.fab_lb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#d1fae5' }}>{fmtFt(shopTotals.installed_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#d1fae5' }}>{fmtFt(shopTotals.installed_lb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fecaca' }}>{fmtFt(shopTotals.total_sb_ft - shopTotals.installed_sb_ft)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fecaca' }}>{fmtFt(shopTotals.total_lb_ft - shopTotals.installed_lb_ft)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.total_isos) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gFlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gIlb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTsb - gIsb)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(gTlb - gIlb)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderShopSummary = () => {
    if (shopSummaryLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading shop summary...</div>
          <div style={{ fontSize: '0.85rem' }}>Aggregating fabrication shop data</div>
        </div>
      );
    }

    if (!shopSummaryData || !shopSummaryData.rows || shopSummaryData.rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏭</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No Shop Data Available</h3>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Assign fabrication shops to items in the matrix to see shop analytics.</p>
        </div>
      );
    }

    const { rows, summary } = shopSummaryData;
    const gs = summary;
    const totalFt = (parseFloat(gs.total_sb_ft) || 0) + (parseFloat(gs.total_lb_ft) || 0);
    const fabFt = (parseFloat(gs.fab_sb_ft) || 0) + (parseFloat(gs.fab_lb_ft) || 0);
    const fabPct = totalFt > 0 ? ((fabFt / totalFt) * 100).toFixed(1) : '0.0';

    const thS = { padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const tdS = { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px' };
    const getProgressColor = (val) => val >= 100 ? colors.success : val >= 50 ? colors.warning : val > 0 ? '#f59e0b' : '#94a3b8';
    const shopColors = ['#1e40af', '#065f46', '#92400e', '#7c3aed', '#be185d', '#0f766e', '#b91c1c', '#4338ca', '#0369a1', '#6d28d9'];

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <MetricCard title="Fab Shops" value={parseInt(gs.shop_count) || 0} icon="🏭" color={colors.primary} />
          <MetricCard title="Total ISOs" value={parseInt(gs.total_isos) || 0} icon="📋" color={colors.info} />
          <MetricCard title="Total Footage" value={fmtFt(totalFt) + ' ft'} icon="📏" color="#1e40af" />
          <MetricCard title="Fabricated" value={fmtFt(fabFt) + ' ft'} subtitle={`${fabPct}% of total`} icon="🔧" color={colors.warning} />
          <MetricCard title="Shipped" value={parseInt(gs.shipped_count) || 0} subtitle={`${gs.total_isos > 0 ? ((parseInt(gs.shipped_count) / parseInt(gs.total_isos)) * 100).toFixed(1) : 0}%`} icon="🚚" color="#0f766e" />
          <MetricCard title="Received" value={parseInt(gs.received_count) || 0} subtitle={`${gs.total_isos > 0 ? ((parseInt(gs.received_count) / parseInt(gs.total_isos)) * 100).toFixed(1) : 0}%`} icon="📦" color={colors.success} />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ ...thS, textAlign: 'left' }}>Fabrication Shop</th>
                  <th style={thS}>ISOs</th>
                  <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Total FT</th>
                  <th style={{ ...thS, backgroundColor: '#fefce8' }}>Fabricated FT</th>
                  <th style={{ ...thS, backgroundColor: '#fef2f2' }}>Remaining FT</th>
                  <th style={thS}>Fab %</th>
                  <th style={thS}>Complete</th>
                  <th style={thS}>In Progress</th>
                  <th style={thS}>Not Started</th>
                  <th style={thS}>Shipped</th>
                  <th style={thS}>Received</th>
                  <th style={{ ...thS, width: '150px' }}>Fabrication Progress</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const rowTotalFt = (parseFloat(row.total_sb_ft) || 0) + (parseFloat(row.total_lb_ft) || 0);
                  const rowFabFt = (parseFloat(row.fab_sb_ft) || 0) + (parseFloat(row.fab_lb_ft) || 0);
                  const rowRemFt = rowTotalFt - rowFabFt;
                  const rowFabPct = rowTotalFt > 0 ? Math.round((rowFabFt / rowTotalFt) * 1000) / 10 : 0;
                  const shopColor = shopColors[idx % shopColors.length];

                  return (
                    <tr key={row.shop} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', fontWeight: '600', color: row.shop === 'Not Assigned' ? '#94a3b8' : shopColor, borderLeft: `3px solid ${row.shop === 'Not Assigned' ? '#94a3b8' : shopColor}` }}>{row.shop}</td>
                      <td style={tdS}>{parseInt(row.total_isos) || 0}</td>
                      <td style={{ ...tdS, backgroundColor: '#f8faff', fontWeight: '500' }}>{fmtFt(rowTotalFt)}</td>
                      <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtFt(rowFabFt)}</td>
                      <td style={{ ...tdS, backgroundColor: '#fef8f8' }}>{fmtFt(rowRemFt)}</td>
                      <td style={{ ...tdS, fontWeight: '600', color: getProgressColor(rowFabPct) }}>{rowFabPct}%</td>
                      <td style={{ ...tdS, color: colors.success, fontWeight: '500' }}>{parseInt(row.shop_complete_count) || 0}</td>
                      <td style={{ ...tdS, color: colors.warning, fontWeight: '500' }}>{parseInt(row.shop_in_progress_count) || 0}</td>
                      <td style={{ ...tdS, color: '#94a3b8' }}>{parseInt(row.shop_not_started_count) || 0}</td>
                      <td style={tdS}>{parseInt(row.shipped_count) || 0}</td>
                      <td style={tdS}>{parseInt(row.received_count) || 0}</td>
                      <td style={{ ...tdS, padding: '8px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ flex: 1, height: '14px', backgroundColor: '#e2e8f0', borderRadius: '7px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(rowFabPct, 100)}%`, height: '100%', backgroundColor: getProgressColor(rowFabPct), borderRadius: '7px', transition: 'width 0.3s ease' }} />
                          </div>
                          <span style={{ fontSize: '10px', color: '#64748b', minWidth: '30px', textAlign: 'right' }}>{rowFabPct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                  <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.total_isos) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(totalFt)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(fabFt)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fmtFt(totalFt - fabFt)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{fabPct}%</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.shop_complete_count) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{(parseInt(gs.total_isos) || 0) - (parseInt(gs.shop_complete_count) || 0) - rows.reduce((s, r) => s + (parseInt(r.shop_not_started_count) || 0), 0)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{rows.reduce((s, r) => s + (parseInt(r.shop_not_started_count) || 0), 0)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.shipped_count) || 0}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(gs.received_count) || 0}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1, height: '14px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '7px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(parseFloat(fabPct), 100)}%`, height: '100%', backgroundColor: colors.success, borderRadius: '7px' }} />
                      </div>
                      <span style={{ fontSize: '10px', minWidth: '30px', textAlign: 'right' }}>{fabPct}%</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <div style={{ padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.primary} 100%)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📈 {disciplineName} Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Progress tracking, completion trends, and breakdown</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            {isPiping && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', backgroundColor: asOfDate ? 'rgba(251,191,36,0.95)' : 'rgba(255,255,255,0.18)', borderRadius: '8px', padding: '0.3rem 0.5rem' }} title="Reconstruct analytics as they stood at end of a past day">
                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: asOfDate ? '#78350f' : 'white' }}>📅 As of</span>
                <input
                  type="date"
                  value={asOfDate || ''}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setAsOfDate(e.target.value || null)}
                  style={{ border: 'none', borderRadius: '6px', padding: '0.25rem 0.4rem', fontSize: '0.8rem', color: '#1e293b' }}
                />
                {asOfDate && (
                  <button onClick={() => setAsOfDate(null)} title="Back to live data" style={{ background: '#78350f', color: 'white', border: 'none', borderRadius: '6px', padding: '0.25rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>✕ Live</button>
                )}
              </div>
            )}
            <button onClick={handleOpenAuditLog} style={{ padding: '0.5rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.3rem' }} title="View recent changes audit log">
              📋 Audit Log
            </button>
            <button onClick={() => setShowEmailModal(true)} style={{ padding: '0.5rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.3rem' }} title="Email report as PDF">
              📧 Email
            </button>
            <button onClick={handleDownloadPdf} style={{ padding: '0.5rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.3rem' }} title="Download report as PDF">
              📥 PDF
            </button>
            <button onClick={handleDownloadExcel} style={{ padding: '0.5rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.3rem' }} title="Download report as Excel">
              📊 Excel
            </button>
            <SettingsButton onClick={() => setShowSettings(true)} />
            <button onClick={onClose} style={{ padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>✕ Close</button>
          </div>
        </div>

        {asOfDate && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', backgroundColor: '#fef3c7', borderBottom: '1px solid #fde68a', color: '#78350f', fontSize: '0.85rem', fontWeight: '600' }}>
            🕓 Viewing data as of {new Date(`${asOfDate}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} (end of day) — reconstructed from change history
            {asOfLoading && <span style={{ fontWeight: '400', opacity: 0.8 }}>· loading…</span>}
            {asOfError && <span style={{ fontWeight: '400', color: '#991b1b' }}>· reconstruction failed, showing live data — {asOfError}</span>}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', alignItems: 'flex-end' }}>
          {tabs.filter(tab => !hiddenTabs.has(tab.id)).map(tab => (
            <button key={tab.id} onClick={() => handleSelectTopTab(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: activeTopTab === tab.id ? colors.success : 'transparent',
              color: activeTopTab === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
          <div style={{ position: 'relative', marginLeft: 'auto', marginBottom: '0.5rem' }}>
            <button
              onClick={() => setShowTabPicker(prev => !prev)}
              style={{
                padding: '6px 12px', backgroundColor: showTabPicker ? '#374151' : '#f1f5f9',
                color: showTabPicker ? '#fff' : '#64748b', border: '1px solid #e2e8f0',
                borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}
              title="Choose which tabs to show"
            >
              {hiddenTabs.size > 0 && <span style={{ backgroundColor: '#ef4444', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{hiddenTabs.size}</span>}
              Tabs
              <span style={{ fontSize: '10px' }}>{showTabPicker ? '▲' : '▼'}</span>
            </button>
            {showTabPicker && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '4px', zIndex: 100,
                backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid #e2e8f0', padding: '8px 0', minWidth: '200px', maxHeight: '320px', overflowY: 'auto',
              }}>
                <div style={{ padding: '4px 12px 8px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Show/Hide Tabs</span>
                  {hiddenTabs.size > 0 && (
                    <button onClick={resetTabVisibility} style={{ fontSize: '11px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '500' }}>Show All</button>
                  )}
                </div>
                {tabs.map(tab => (
                  <React.Fragment key={tab.id}>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
                      cursor: 'pointer', fontSize: '13px', color: '#374151',
                      backgroundColor: hiddenTabs.has(tab.id) ? '#fef2f2' : 'transparent',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = hiddenTabs.has(tab.id) ? '#fee2e2' : '#f8fafc'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = hiddenTabs.has(tab.id) ? '#fef2f2' : 'transparent'; }}
                    >
                      <input
                        type="checkbox"
                        checked={!hiddenTabs.has(tab.id)}
                        onChange={() => toggleTabVisibility(tab.id)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{tab.icon}</span>
                      <span style={{ fontWeight: hiddenTabs.has(tab.id) ? '400' : '500', color: hiddenTabs.has(tab.id) ? '#9ca3af' : '#374151' }}>{tab.label}</span>
                    </label>
                    {(subTabConfig[tab.id] || []).map(st => {
                      const sid = subTabId(st);
                      const isHidden = hiddenTabs.has(sid);
                      return (
                        <label key={sid} style={{
                          display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px 4px 34px',
                          cursor: 'pointer', fontSize: '12px',
                          backgroundColor: isHidden ? '#fef2f2' : 'transparent',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = isHidden ? '#fee2e2' : '#f8fafc'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = isHidden ? '#fef2f2' : 'transparent'; }}
                        >
                          <input
                            type="checkbox"
                            checked={!isHidden}
                            onChange={() => toggleTabVisibility(sid)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ color: '#cbd5e1' }}>└</span>
                          <span style={{ fontWeight: '400', color: isHidden ? '#9ca3af' : '#64748b' }}>{st.label}</span>
                        </label>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {activeSubTabs.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1.5rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', alignItems: 'center', flexWrap: 'wrap' }}>
            {activeSubTabs.map((st) => {
              const active = isSubTabActive(st);
              return (
                <button
                  key={st.label}
                  onClick={() => handleSelectSubTab(st)}
                  style={{
                    padding: '0.4rem 1rem',
                    backgroundColor: active ? colors.primary : 'white',
                    color: active ? 'white' : '#64748b',
                    border: active ? 'none' : '1px solid #e2e8f0',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600',
                    transition: 'all 0.2s',
                  }}
                >
                  {st.label}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRows === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'quantity' && isPiping && renderQuantity()}
              {selectedView === 'quantity2' && isPiping && renderQuantity2()}
              {selectedView === 'quantity3' && isPiping && renderQuantity3()}
              {selectedView === 'quantity4' && isPiping && renderQuantity4()}
              {selectedView === 'material' && isPiping && renderMaterial()}
              {selectedView === 'shopSummary' && isPiping && renderShopSummary()}
              {selectedView === 'shopTabs' && isPiping && <ShopAnalyticsTabs projectId={projectId} activeTab={shopAnalyticsTab} hideTabBar asOf={asOfDate} />}
              {selectedView === 'service' && renderService()}
              {selectedView === 'sov' && renderSOV()}
              {selectedView === 'sovAnalytics' && isPiping && <PipingSOVAnalytics projectId={projectId} asOf={asOfDate} />}
              {selectedView === 'sovWhatIf' && isPiping && <PipingSOVAll projectId={projectId} asOf={asOfDate} />}
              {selectedView === 'onHold' && renderOnHold()}
            </>
          )}
        </div>

        <div style={{ padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
          <span>{analytics.totalRows} items</span>
          <span>{analytics.hasCreditSplit
            ? `Shop: ${formatNumber(analytics.shopCreditProgress)}% | Field: ${formatNumber(analytics.fieldCreditProgress)}%`
            : `Avg: ${formatNumber(analytics.avgProgress)}%`} | Completed: {analytics.completed} | In Progress: {analytics.inProgress}{analytics.onHoldCount > 0 ? ` | On Hold: ${analytics.onHoldCount}` : ''}</span>
        </div>
      </div>
      
      <AnalyticsSettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        updateSetting={updateSetting}
        toggleMetric={toggleMetric}
        toggleChart={toggleChart}
        setChartType={setChartType}
        resetToDefaults={resetToDefaults}
        availableMetrics={AVAILABLE_METRICS}
        availableCharts={AVAILABLE_CHARTS}
      />

      {showEmailModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEmailModal(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: colors.dark }}>📧 Email Analytics Report</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 0.75rem' }}>
              Enter email addresses separated by commas. A PDF report will be attached.
            </p>
            <textarea
              value={emailAddresses}
              onChange={(e) => setEmailAddresses(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              style={{ width: '100%', minHeight: '80px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {emailResult && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
                backgroundColor: emailResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: emailResult.type === 'success' ? '#059669' : '#dc2626' }}>
                {emailResult.message}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={() => { setShowEmailModal(false); setEmailResult(null); }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                Cancel
              </button>
              <button onClick={handleSendEmail} disabled={emailSending || !emailAddresses.trim()}
                style={{ padding: '0.5rem 1rem', backgroundColor: emailSending ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: emailSending ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: '500' }}>
                {emailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAuditLog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuditLog(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '900px', width: '95%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>📋 Recent Changes Audit Log</h3>
              <button onClick={() => setShowAuditLog(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {auditLogLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading audit log...</div>
              ) : !auditLogData || auditLogData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No recent changes found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151' }}>ISO</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151' }}>Shop</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151' }}>Material</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151' }}>Modified By</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: '11px', fontWeight: '700', color: '#374151' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogData.map((row, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', fontWeight: '600' }}>{row.iso_sheet_number || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>{row.fabrication_shop || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>{row.material_1 || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', color: '#6366f1' }}>{row.last_modified_by || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', color: '#64748b' }}>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressAnalyticsDashboard;
